/* eslint-disable @typescript-eslint/no-unused-vars */
import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { AxiosProgressEvent } from 'axios';
import { HISTORY_ROLE } from '../components/common';
import { UploadProcesser } from '../components/downloads/UploadProcesser';
import { Severity } from '../components/mplat/Dashboard/DashboardMessage';
import { OffsetLimit, Profile, StatusPagesResponse, StatusResponse } from './api';
import { baseQueryWithReauth } from './reauth';
import { SdnOperationTaskStatus } from './sdn';
import { AccessRequestStatus, AccountListElement, AccountResponse, AccountResType, AccountShort, OrgMinimal, ReliableType, TariffGroup, UserProfileMinimal, UserRequest, UserResponse, Validity } from './users_v1';

export enum AccessRequestTask {
    NOTSET = 0, // _("CONST-001030", "notset")
    CONTACT = 1, // _("CONST-001031", "Связаться")
    PROPOSAL = 2, // _("CONST-001032", "Дать КП")
    CONCLUDE = 3, // _("CONST-001033", "Заключить договор")
    WAIT = 4, // _("CONST-001034", "Подождать")
    DISCINSIDE = 5, // _("CONST-001035", "Обсудить внутри")
}

export enum AccessRequestType {
    UNKNOWN = 0, // 'unkbown'
    MEDIA = 1, // 'media'
    TV = 2, // 'tv'
    PRODUCT = 4, // 'product'
}

export type BTechGraphType = 'iat' | 'bitr';

export interface BTechLimit {
    iat: {
        warn_min: number
        warn_max: number,
        err_min: number,
        err_max: number,
        no_sig_limit: number,
    },
    bitr: {
        warn_min: number
        warn_max: number,
        err_min: number,
        err_max: number,
    }
}

export interface BTechData {
    code: number,
    INIT_LIMITS: BTechLimit,
    UPDATED: number,
    types: string[],
    data: number[][][],
    stat: {
        nosig_errs: number,
        iat_warns: number,
        mlr_errs: number,
        mlr_warns: number,
        iat_errs: number,
    },
    SPLIT_CFG: {
        bitr: number[][],
        iat: number[][],
    },
    label: string[],
}

export enum ContentType {
    UNIT = 1,
    TV = 2,
    SERVICE = 4,
}

export enum ContractType {
    LICENSE = 1, // _("CONST-010-3001", "Лицензионный")
    PRODUCT = 2, // _("CONST-010-3002", "На услуги")
}

export enum ContractStatus {
    UNKNOWN = 0, // "Неизвестно"
    CONCLUDED = 1, // _("CONST-010-1001", "Заключен")
    AGREEMENT = 2, // _("CONST-010-1002", "Согласование")
    TERMINATED = 3, // _("CONST-010-1003", "Расторгнут")
}

export type FileType = 'cover' | 'trailer' | 'media';

export enum FSObjectCategory {
    MEDIAFILE = 1, // _("CONST-FILES-000020", "Media File")
    COVER = 2, // _("CONST-FILES-000021", "Cover File")
    TRAILER = 3, // _("CONST-FILES-000022", "Trailer File")
    TRANSCODED = 4, // _("CONST-FILES-000023", "Transcoded File")
    AVATAR = 5, // _("CONST-FILES-000024", "Avatar File")
    LINK = 6, // _("CONST-FILES-000025", "File Link")
    CACHED = 7, // _("CONST-FILES-000026", "Cache File")
    TEMPORARY = 8, // _("CONST-FILES-000027", "Temporary File")
    DOCUMENT = 9, // _("CONST-FILES-000028", "Document File")
}

export enum FSObjectState {
    ONLINE = 0, // _("CONST-FILES-000030", "Online")
    UNLINKED = 1, // _("CONST-FILES-000031", "Unlinked")
}

export enum LicenseAgreement {
    NO = 1,
    YES = 2,
    PARTIAL = 3,
}

export enum PrefferedSource {
    SATELLITE = 1, //_("CONST-001001", "satellite")
    LAND = 2, //_("CONST-001002", "land")
    MEDIALOGISTIKA = 3, //_("CONST-001003", "medialogistika",)
    OTHER = 4, //_("CONST-001004", "other")
    ANY = 5, //_("CONST-001005", "any")
}

export enum SourcesType {
    SATELLITE = 1,
    LAND = 2,
    MEDIALOGISTIKA = 3,
    OTHER = 4,
    ANY = 5,
}

export enum AccessPermissionStatus {
    WORKING = 0, // "working"
    ISSUED = 1, // "issued"
    REVOKED = 2, // "revoked"
}

type Enumerate<N extends number, Acc extends number[] = []> = Acc['length'] extends N
    ? Acc[number]
    : Enumerate<N, [...Acc, Acc['length']]>

type IntRange<F extends number, T extends number> = Exclude<Enumerate<T>, Enumerate<F>>

export type HexColor = IntRange<0, 255>

type oneToNine = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
type zeroToNine = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
type YYYY = `19${zeroToNine}${zeroToNine}` | `20${zeroToNine}${zeroToNine}`
type MM = `0${oneToNine}` | `1${0 | 1 | 2}`
type DD = `${0}${oneToNine}` | `${1 | 2}${zeroToNine}` | `3${0 | 1}`
export type RawDateString = `${YYYY}-${MM}-${DD}`;

export type CONTENT_FIELD =
    // common
    'unit' |
    'qualities' |
    'collections' |
    'languages' |
    'subtitles' |
    'countries' |
    'description' |
    'tags' |
    'covers' |
    'trailers' |
    // tv
    'mmchannel' |
    'mmcertno' |
    'mmowner' |
    'licno' |
    'licowner' |
    'tvthemes' |
    'rowners' |
    'ssources' |
    // unit
    'unit_type' |
    'startyear' |
    'genres' |
    'techinfo' |
    'links' |
    'mediafiles' |
    'rights_area' |
    'rights_cat';

export enum PERMISSIONS {
    CHANGE = 'change',
    DELETE = 'delete',
    VIEW = 'view',
    DOWNLOAD = 'download',
    UPLOAD = 'upload',
    ANY = 'any',
}

export enum TYPS {
    NORMAL = 1, // "normal"
    DRAFT = 2, // "draft"
    DELETED = 4, // "deleted"
    CENSORED = 8, // "censored"
}

export enum ACCESS_TYPES {
    NOBODY = 0, // "nobody"
    ALL = 1, // "all"
    FAVORITES = 2, // "favorites"
    LIST = 3, // "list"
}

export enum UploadCodes {
    ERROR = -3, // 'waiting',
    SUCCESS = -2, // 'waiting',
    WAITING = -1, // 'waiting',
    UPLOADING = 0, // 'uploading',
    STOPPED = 1, // 'stopped',
    PAUSED = 2, // 'paused',
    BUILDING = 3, // 'building',
    COMPLETED = 4, // 'completed',
    FAILED = 5, // 'failed',
}

export enum xUploadState {
    CREATED = 'created',
    PROCESSED = 'processed',
    CONVERTED = 'converted',
    PACKED = 'packed',
    FINISHED = 'finished',
    FAILED = 'failed',
}

export interface AccessControlEntry {
    permission: PERMISSIONS,
    action: 'allow' | 'deny',
    readonly: boolean,
    user?: { id: number, username: string, company: string },
    group?: { cname: string, name: string },
}

export interface AccessControlEntryRequest {
    permission: PERMISSIONS,
    action: 'allow' | 'deny',
    user?: string,
    group?: string,
}

export interface AccessPermissionUnitSignal {
    id: number,
    name: string,
    source: number,
    fmt: number
}

export interface AccessPermissionUnit {
    fmt: {
        id: number,
        name: string,
    }[],
    unit: {
        id: number,
        ctype: ContentType,
        licno: string,
        owner: number,
        title: string,
        mm: number,
        mmcertno: string,
        mmchannel: string,
        covers: string[],
        category: {
            id: number,
            name: string,
        },
    },
    version: string | null,
    signals: (AccessPermissionUnitSignal | number)[],
}

export enum AccessPermissionExt {
    NORMAL = 1, //_("CONST-001056", "Обычное разрешение")
    EXTERNAL = 2, //_("CONST-001057", "Внешнее разрешение")
}

export enum AccessPermissionType {
    TV = 1, //_("CONST-001046", "Разрешение на телеканалы")
    MEDIA = 2, //_("CONST-001047", "Разрешение на контент")
    PRODUCT = 3, //_("CONST-001048", "Разрешение на услуги")
}

export interface AccessPermission {
    id: number,
    aptype: AccessPermissionType,
    external: AccessPermissionExt,
    origin: number,
    status: AccessPermissionStatus,
    validity: {
        lower: string | null,
        upper: string | null,
        bounds: string
    },
    issuer: AccountListElement,
    issuerperson: PersonDescription,
    requestperson: PersonDescription,
    recipient: AccountListElement,
    details: {
        // havelic: false,
        licno: string,
        licdate: string,
        ontest: number,
        sources: { stype: SourcesType, params?: string, sources: { id: number, name: string, info?: { org?: string } }[], }[],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additional: any,
        prolonged?: number,
    },
    units: AccessPermissionUnit[],
    added: string,
    updated: string,
    accessrequest: { id: number, account_id: number } | null
    issuer_accs: AccountListElement[],
    revoked: string | null,
    issued: string | null,

    contracts: Contract[],
    comment: string,
    tvsrcpartners: {
        id: number,
        org: OrgMinimal,
        logo: string,
        name: string,
        owner: PersonDescription,
        rtype: AccountResType,
        srcids: number[],
        nicname: string | null,
        sources: number[]
    }[],
    files: FileInfo[]
    // person: PersonDescription,
    // reqperson: PersonDescription,
    // is_favorites: false,
    is_prolonged: boolean,
}

export interface AccessPermissionFull extends AccessPermission {
    accessrequest: {
        name: string | null,
        group: number,
        subid: number | null,
        artype: AccessRequestType,
        owner: AccountDescription,
        person: PersonDescription,
        consumer: AccountDescription,
        details: {
            licagr: number,
            ontest: number | null,
            pstype: number,
            psdetails: string,
        },
        added: string,
        updated: string,
        id: number,
        account_id: number,
    } | null,
}

export interface AccessPermissionRequest {
    id?: number,
    aptype?: AccessPermissionType,
    external?: AccessPermissionExt,
    issuer: number,
    recipient: number,
    accessrequest?: { id: number, account_id: number },
    grantaccreq?: boolean,
    validity?: { lower?: string, upper?: string },
    details: {
        havelic: boolean,
        licno: string,
        licdate: string | null,
        ontest: number | null,
        sources: { stype: SourcesType, params?: string, source?: number[] }[],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additional?: any;
        prolonged?: number,
    },
    units: { unit: number, fmt: number[], version: string | null }[],
    accs?: number[],
    contracts: number[],
    files?: number[],
}

export interface AccessResponseOld {
    id: number,
    user: {
        id: number,
        username: string,
        email: string,
        profile: Profile,
        groups?: string[]
    },
    content: number,
    content_title: string,
    content_covers: string[],
    content_owner: {
        id: number,
        username: string,
        email: string,
        profile: Profile
        groups?: string[] // для унификации с user
    },
    messages: [
        {
            user: string,
            message: string,
            timestamp: string,
            status: string,
        }
    ],
    status: AccessRequestStatus
}

export interface AccountDescription {
    id: number,
    rtype: number,
    name: string,
    email: string,
    contact: string,
    phone: string,
    logo: string | null,
    nickname: string | null
}

export interface ContractRequest {
    id: number
    consumer: number, //  - Потребитель
    provider: number, //  - Поставщик (кто заводит договор)
    cotype: ContractType, //    - тип договора:
    number: string, //      - Номер договора
    validity?: { lower?: string, upper?: string }, //    - Срок действия
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additional: any, //  - Дополнительные условия
    comment: string, //     - Комментарий
    content: number[], //     - список прикрепленных единиц контента
    products: number[], //    - список прикрепленных продуктов
    files: number[] //        - список прикрепленных файлов
    status?: ContractStatus
}

export interface Contract {
    id: number,
    number: string,
    validity: Validity | null,
    additional: string,
    comment: string,
    consumer: AccountListElement,
    provider: AccountListElement,
    content?: {
        id: number,
        ctype: ContentType,
        title: string,
        category: { id: number, name: string } | null,
        covers: string[]
    }[],
    permits: number[],
    files: FileInfo[],
    cotype: ContractType,
    status: ContractStatus,
    editable: boolean,
}

export interface CrmSourceConsumer {
    consumer: AccountListElement,
    units: number,
    crm_status: CrmStatus
}

export interface CrmSourceConsumerGroup {
    unit: {
        id: number,
        ctype: ContentType,
        title: string,
        category: {
            id: number,
            name: string
        },
        covers: string[],
        mm_id: number
    },
    accesspermission: {
        id: number,
        status: number,
        issued: string,
        revoked: string | null,
        validity: Validity,
        params: {
            fmt: {
                id: number,
                name: string,
            }[],
            version: string
        },
        crm_status: CrmStatus,
        sources: {
            id: number,
            name: string
        }[]
    },
    signals: {
        id: number,
        state: TVSignalStateStatus,
        ctvsignal: {
            id: number,
            name: string,
            owner: AccountListElement,
            source: {
                id: number,
                label: string,
                name: string
            },
            quality: {
                id: number,
                label: string,
                name: string
            },
            tvchannel: number
        },
        crm_status: CrmStatus
    }[]
}

export interface FileInfo {
    id: number,
    cat: FSObjectCategory,
    name: string,
    size: number,
    url: string,
}

export interface PersonDescription {
    id: number,
    nickid: string,
    email: string,
    hide_email: boolean,
    firstname: string,
    surname: string | null,
    lastname: string,
    phone: string,
    birthday: string,
    avatar: string,
    hide_fio: boolean,
    hide_phone: boolean,
    hide_birthday: boolean,
}

export interface AccessResponse {
    id: number,
    account_id: number,
    r_type: number,
    name: string | null,
    group: number,
    subid: number | null,
    artype: AccessRequestType,
    units_cnt: number,
    products_cnt: number,
    owner: AccountListElement,
    person: PersonDescription
    consumer: AccountListElement,
    provider: AccountListElement,
    recipient: AccountListElement,
    units: {
        id: number,
        ctype: ContentType,
        title: string,
        title_eng: string,
        owner: AccountListElement
        covers: string[]
        category: { id: number, name: string } | null
    }[],
    products: { // - заказанные услуги
        id: number,
        name: string,
        types: {
            id: number,
            name: string
        }[],
        cover: string
    }[],
    added: string,
    updated: string,
    status: AccessRequestStatus,
    is_favorites: boolean,
    permissions: {
        id: number,
        status: number,
        validity: {
            lower: string | null,
            upper: string | null,
            bounds: string
        }
    }[],
    additional: {
        sendby: {
            account: AccountListElement,
            status: AccessRequestStatus,
        },
        tvbrdcsterr: number[], // - территория вещания, справвочник TVBRCSTERR
        tvdienv: number[],
    },
    comment: string | null,
    task: AccessRequestTask | null
    contracts: number[],
    promocodes: string[],
    prefixes?: string[],
}

export interface AccessFull extends AccessResponse {
    artype: AccessRequestType,
    units_cnt: number,
    iscreated: boolean,
    details: {
        licagr: LicenseAgreement,
        ontest: number | null,
        pstype: SourcesType,
        psdetails: string,
        promocodes: string[],
        // promcod?: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additional: any,
        sources?: {
            stype: SourcesType,
            params: string,
            sources: {
                id: number,
                name: string,
                info: {
                    org: string,
                    pdfdisplay: {
                        template: string,
                        addnewline: boolean
                    }
                }
            }[]
        }[]
    },
    // request: {
    //     sender: {
    //         accounts: number[]
    //     }
    // },
    // messages: {
    //     user: string,
    //     status: AccessRequestStatus,
    //     message: string,
    //     timestamp: string
    // }[],
    updated: string,
}

export interface AccessMessage {
    id: number,
    added: string,
    sender: AccountListElement,
    person: PersonDescription,
    recipient: AccountListElement,
    message: string,
}

export interface AccessHistory {
    id: number,
    added: string,
    action: number,
    account: AccountListElement,
    person: PersonDescription,
    act_data: {
        recipients: AccountListElement[]
    }
}

export interface AccessSummaryResponse {
    summary: {
        all: number,
        opened: number,
        pending: number,
        granted: number,
        granted_downloaded: number,
        rejected: number,
        canceled: number
    }
}

export interface CheckMark {
    checked: boolean | null,
    updated: string | null,
    user: {
        id: number,
        nickid: string,
        email: string,
        hide_email: boolean,
        profile: {
            firstname: string,
            surname: string,
            lastname: string,
            phone: string,
            avatar: string | null,
            hide_fio: boolean,
            hide_phone: boolean,
        },
    } | null,
}

export interface Banner {
    id: number,
    title: string,
    url: string,
    color: string | null,
    published: string,
    files: string[],
    tags: string[],
    newtab: boolean,
    pinned: number,
}

export interface BannersAdmin {
    id: number,
    pinned: number,
    priority: number,
    title: string,
    newtab: boolean,
    content: {
        ru: {
            enabled: boolean | null,
            title: string,
            url: string,
            color: string | null,
            files: string[],
            tags: string[],
        },
        en: {
            enabled: boolean | null,
            title: string,
            url: string,
            color: string | null,
            files: string[],
            tags: string[],
        }
    }
    tags: string[],
}

export interface BPlayer {
    enabled: boolean | null, // - вкл/выкл может выставлять как админ так и поставщик. Если не настроено player.configured = false выдаст ошибку
    player: { // - настройки плеера, исправлять может только админ. Если трансляции выключены отображает только состояние configured, если вкл - все параметры
        type: number, // - тип 1-Инвентос/2 - какойнибудь другой (пока проставляем 1)
        url: string, // - url трансляции, не знаю для инвентоса вроде достаточно url указать, но сам смотри, можешь хоть весь код iframe-а писать
        configured: boolean // - read-only признак того что параметры плеера настроены
    }
}

export interface CMSFile {
    id: number,
    name: string,
    file: string
}

export interface CMSFileUpload {
    name: string,
    upload_to: string,
    file: File,
    onProgress?: (e: AxiosProgressEvent) => void,
}

export interface Comment {
    id: number,
    fieldname: CONTENT_FIELD,
    comment: string,
    user: {
        id: number,
        username: string,
        email: string,
        profile: Profile,
    },
    added: string
}

export interface Comments {
    qualities: Comment | null,
    collections: Comment | null,
    languages: Comment | null,
    subtitles: Comment | null,
    countries: Comment | null,
    mmchannel: Comment | null,
    mmcertno: Comment | null,
    mmowner: Comment | null,
    licno: Comment | null,
    licowner: Comment | null,
    tvthemes: Comment | null,
    rowners: Comment | null,
    description: Comment | null,
    tags: Comment | null,
    covers: Comment | null,
    trailers: Comment | null,
    ssources: Comment | null,
    unit: Comment | null
}

export interface ContentCms {
    id?: number,
    adm_visible_to_guests: boolean,
    adm_show_on_welcome: boolean,
    adm_promo_on_welcome: boolean,
    adm_priority: number,
}

export interface ContentRequest {
    id?: number,
    title: string,
    title_eng: string | null,
    unit_type?: string,
    collections?: number[],
    startyear?: number,
    genres?: number[],
    countries?: string[],
    qualities?: string[],
    owner?: number // Pick<UserRequest, 'email' | 'username' | 'profile'>
    // covers?: { name: string, url: string }[]
    covers?: (string | number)[]
    trailers?: (string | number)[]
    permissions?: PERMISSIONS[],
    view_access: ACCESS_TYPES,
    view_accesslist: number[],
    download_access: ACCESS_TYPES,
    download_accesslist: number[],
    languages: string[],
    subtitles: string[],
    description: string,
    techinfo: string,
    links: { kinopoisk?: string, imdb?: string },
    tags: number[],
    version: number,
    typ?: TYPS,
    offers?: number[],
    autofill?: boolean,
    rights_area: number[],
    rights_cat: number[],
    agerestriction?: number,
    timing?: number,
    epnum?: number,
    card_prefix?: string | null,
    visible_to_guests: boolean,
    vertical: boolean,
    moderation?: {
        sendreport?: boolean,
        comments?: {
            // title?: string | null,
            unit_type?: string | null,
            qualities?: string | null,
            collections?: string | null,
            startyear?: string | null,
            genres?: string | null,
            languages?: string | null,
            subtitles?: string | null,
            countries?: string | null,
            description?: string | null,
            techinfo?: string | null,
            links?: string | null,
            tags?: string | null,
            covers?: string | null,
            trailers?: string | null,
            rights_area?: string | null,
            rights_cat?: string | null,
            unit?: string | null,
            agerestriction?: string | null,
        },
        checkmarks?: {
            unit?: boolean | null,
            title?: boolean | null,
            unit_type?: boolean | null,
            qualities?: boolean | null,
            collections?: boolean | null,
            startyear?: boolean | null,
            genres?: boolean | null,
            languages?: boolean | null,
            subtitles?: boolean | null,
            countries?: boolean | null,
            description?: boolean | null,
            techinfo?: boolean | null,
            links?: boolean | null,
            tags?: boolean | null,
            covers?: boolean | null,
            trailers?: boolean | null,
            rights_area?: boolean | null,
            rights_cat?: boolean | null,
            agerestriction?: boolean | null,
        }
    },
}

export interface ContentTVRequest {
    id?: number,
    title: string,
    title_eng?: string,
    collections?: number[],
    tags?: number[],
    description?: string,
    tvqualities?: number[],
    languages?: number[],
    countries?: number[],
    subtitles?: number[],
    tvthemes?: number[],
    ptype: number,
    mmchannel?: string,
    mmcertno?: string,
    mmowner: number,
    licno?: string,
    licowner: number,
    category?: number | null,
    bcdelivery?: boolean,
    testperiod?: number,
    ssources?: {
        sources: number[], // userbooks типа tvsat
        label: string, // уникальная метка, сам придумываешь
        active: boolean,
        stype: number,
        sclass?: number,
        params: string | null
    }[],
    rowners?: number[],
    offers?: number[],
    owner: number,
    view_access?: number,
    view_accesslist?: number[],
    typ?: number,
    version?: number,
    autofill?: boolean,
    agerestriction?: number,
    mm?: number,
    rsterritories?: number[],
    rsmedium?: number[],
    tvbrdcsterr?: number[],
    tvdienv?: number[],
    bplayer?: {
        enabled: boolean | null,
        player?: {
            type: number | null,
            url: string
        }
    },
    visible_to_guests: boolean,
    covers?: (string | number)[]
    trailers?: (string | number)[]
    card_prefix?: string,
    moderated?: boolean,
    moderation?: {
        sendreport?: boolean,
        comments?: {
            qualities?: string | null,
            collections?: string | null,
            languages?: string | null,
            subtitles?: string | null,
            countries?: string | null,
            description?: string | null,
            tags?: string | null,
            covers?: string | null,
            trailers?: string | null,
            rights_area?: string | null,
            rights_cat?: string | null,
            unit?: string | null,
            mmchannel?: string | null,
            mmcertno?: string | null,
            mmowner?: string | null,
            licno?: string | null,
            licowner?: string | null,
            tvthemes?: string | null,
            rowners?: string | null,
            ssources?: string | null,
            agerestriction?: string | null,
            rsterritories?: string | null,
            rsmedium?: string | null,
            tvbrdcsterr?: string | null,
            tvdienv?: string | null,
        },
        checkmarks?: {
            unit?: boolean | null,
            title?: boolean | null,
            qualities?: boolean | null,
            collections?: boolean | null,
            languages?: boolean | null,
            subtitles?: boolean | null,
            countries?: boolean | null,
            description?: boolean | null,
            tags?: boolean | null,
            covers?: boolean | null,
            trailers?: boolean | null,
            rights_area?: boolean | null,
            rights_cat?: boolean | null,
            mmchannel: boolean | null,
            mmcertno: boolean | null,
            mmowner: boolean | null,
            licno: boolean | null,
            licowner: boolean | null,
            tvthemes: boolean | null,
            rowners: boolean | null,
            agerestriction?: boolean | null,
            rsterritories?: boolean | null,
            rsmedium?: boolean | null,
            tvbrdcsterr?: boolean | null,
            tvdienv?: boolean | null,
            [key: string]: boolean | null | undefined,
        }
    },
}

export interface ContentUnit {
    id?: number,
    ctype: ContentType,
    title: string,
    title_eng: string | null,
    covers?: { name: string, url: string, id: number, file_id: number, prefix?: string }[],
    owner?: AccountResponse, // Pick<UserRequest, 'id' | 'email' | 'username' | 'profile'>
    view_access: ACCESS_TYPES,
    download_access: ACCESS_TYPES,
    permissions?: PERMISSIONS[],
    published: string,
    moderated: boolean,
    autofill: boolean,
    typ: TYPS,
    accreq: { id: number, account_id: number, status: AccessRequestStatus, issuer_id: number, recipient_id: number }[],
    // accperm: { id: number, status: string, issuer_id: number, recipient_id: number }[],
    ar_cnt: number,
    downloads_cnt: number,
    offers: UserRequest[],
    collections?: { id: number, name: string }[],
    tags: { id: number, name: string }[],
    rights_area: { id: number, name: string }[],
    rights_cat: { id: number, name: string }[],
    genres?: { id: string, name: string }[],
    qualities?: { id: string, name: string }[],
    languages: { id: string, name: string }[],
    countries?: { id: string, name: string }[],
    subtitles: { id: string, name: string }[],
    unit_type?: { id: string, name: string },
    startyear?: number,
    techinfo: string,
    links: { kinopoisk?: string, imdb?: string },
    new: boolean,
    offered: boolean,
    timing?: number,
    epnum?: number,
    agerestriction: { id: number, label: string, name: string } | null,
    visible_to_guests: boolean,
    recommended: boolean,
    adm_visible_to_guests: boolean,
    adm_show_on_welcome: boolean,
    adm_promo_on_welcome: boolean,
    adm_priority: number,
    media:
    {
        cat: number,
        prefix: string,
        root: boolean,
        url: string | null,
        signedurl: string | null,
        type: string
    }[],
    card_prefix: string | null,
    system_prefixes: {
        card: string, // 'account14/Content/Media/Самый лучший фильм/',
        covers: string, // 'account14/Content/Media/Самый лучший фильм/Обложки/',
        trailers: string, // 'account14/Content/Media/Самый лучший фильм/Трейлеры/',
        mediafiles: string, // 'account14/Content/Media/Самый лучший фильм/Медиафайлы/', (только для медиа карточек)
    },
    vertical: boolean,
}

export interface ContentUnitFull extends ContentUnit {
    description: string,
    mediafiles?: MediaFile[],
    trailers: { id: number, name: string, size: number, url: string, signedurl: string }[],
    view_accesslist?: Partial<UserResponse>[],
    download_accesslist?: Partial<UserResponse>[],
    version: number,
    updated: string,
    moderation: {
        comments: {
            title: Comment | null,
            unit_type: Comment | null,
            qualities: Comment | null,
            collections: Comment | null,
            startyear: Comment | null,
            genres: Comment | null,
            languages: Comment | null,
            subtitles: Comment | null,
            countries: Comment | null,
            description: Comment | null,
            techinfo: Comment | null,
            links: Comment | null,
            tags: Comment | null,
            covers: Comment | null,
            trailers: Comment | null,
            mediafiles: Comment | null,
            rights_area: Comment | null,
            rights_cat: Comment | null,
            unit: Comment | null,
            agerestriction: Comment | null,
        },
        checkmarks: {
            title: CheckMark,
            unit_type: CheckMark,
            qualities: CheckMark,
            collections: CheckMark,
            startyear: CheckMark,
            genres: CheckMark,
            languages: CheckMark,
            subtitles: CheckMark,
            countries: CheckMark,
            description: CheckMark,
            techinfo: CheckMark,
            links: CheckMark,
            tags: CheckMark,
            covers: CheckMark,
            trailers: CheckMark,
            mediafiles: CheckMark[],
            rights_area: CheckMark,
            rights_cat: CheckMark,
            agerestriction: CheckMark,
        }
    },
    expire: string | null,
}

export interface ContentTVUnit {
    id: number,
    ctype: ContentType,
    title: string,
    title_eng: string,
    covers?: { name: string, size: number, url: string }[]
    owner: AccountResponse,
    view_access: number,
    download_access: number,
    permissions: PERMISSIONS[],
    published: string,
    moderated: null,
    autofill: boolean,
    typ: number,
    accreq: { id: number, account_id: number, status: AccessRequestStatus, issuer_id: number, recipient_id: number }[],
    // accperm: { id: number, status: AccessRequestStatus, issuer_id: number, recipient_id: number }[],
    active_perm: number | null,
    ar_cnt: number,
    downloads_cnt: number,
    offers: number[],
    collections: { id: number, name: string }[],
    tags: { id: string, name: string }[],
    rights_area: string[],
    rights_cat: string[],
    tvqualities: { id: number, name: string }[],
    languages: { id: string, name: string }[],
    countries: { id: string, name: string }[],
    subtitles: { id: string, name: string }[],
    tvthemes: { id: string, name: string }[],
    ptype: number,
    mm: number,
    mmchannel: string,
    mmcertno: string,
    mmowner: number,
    licno: string,
    licowner: number,
    offered: boolean,
    new: boolean,
    category: { id: number, name: string },
    rowners: number[],
    agerestriction: { id: number, label: string, name: string } | null,
    tvbrdcsterr: { id: number, label: string, name: string }[], // tvbrcsterr - территория вещания
    tvdienv: { id: number, label: string, name: string }[], // tvdienv - среда распространения
    visible_to_guests: boolean,
    recommended: boolean,
    adm_visible_to_guests: boolean,
    adm_show_on_welcome: boolean,
    adm_promo_on_welcome: boolean,
    adm_priority: number,
}

export interface ContentTVSource {
    sources: { id: number, name: string }[] | number[], // userbooks типа tvsat
    label: string, // уникальная метка, сам придумываешь
    active: boolean,
    stype: number,
    sclass: number,
    params: string | null
}

export interface ContentTVUnitFull {
    id: number,
    title: string,
    title_eng: string,
    collections: { id: number, name: string }[],
    tags: { id: number, name: string }[],
    tvqualities: { id: number, name: string }[],
    languages: { id: number, name: string }[],
    countries: { id: number, name: string }[],
    subtitles: { id: number, name: string }[],
    tvthemes: { id: number, name: string, label: string }[],
    ptype: number,
    agerestriction: { id: number, label: string, name: string } | null,
    mmchannel: string | null,
    mmcertno: string | null,
    mmowner: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        org: OrgMinimal,
        owner: UserProfileMinimal,
        logo: string | null
    } | null,
    licno: string | null,
    licowner: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        org: OrgMinimal,
        owner: UserProfileMinimal,
        logo: string | null
    } | null,
    category: {
        id: number,
        name: string
    } | null,
    bcdelivery: boolean,
    testperiod: number,
    ssources: ContentTVSource[],
    rowners: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        org: OrgMinimal,
        owner: UserProfileMinimal,
        logo: string | null
    }[],
    description?: string,
    covers: { id: number, name: string, size: number, url: string }[],
    trailers: { id: number, name: string, size: number, url: string, signedurl: string }[],
    card_prefix: string | null,
    system_prefixes: {
        card: string, // 'account14/Content/Media/Самый лучший фильм/',
        covers: string, // 'account14/Content/Media/Самый лучший фильм/Обложки/',
        trailers: string, // 'account14/Content/Media/Самый лучший фильм/Трейлеры/',
    },
    offers: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        org: OrgMinimal,
        owner: UserProfileMinimal,
        logo: string | null
    }[],
    permissions: PERMISSIONS[],
    owner: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        org: OrgMinimal,
        owner: UserProfileMinimal,
        logo: string | null,
        reliable: ReliableType,
    },
    offered: boolean,
    new: boolean,
    accreq: { id: number, status: AccessRequestStatus, issuer_id: number, recipient_id: number }[] | null,
    // accperm: { id: number, status: AccessRequestStatus, issuer_id: number, recipient_id: number }[],
    active_perm: number | null,
    view_access: number,
    view_accesslist: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        org: OrgMinimal,
        owner: UserProfileMinimal,
        logo: string | null
    }[],
    typ: number,
    version: number,
    published: string,
    updated: string,
    autofill: boolean,
    moderated: null | boolean,
    expire: string | null,
    moderation: {
        sendreport?: boolean,
        comments: {
            qualities?: Comment | null,
            collections?: Comment | null,
            languages?: Comment | null,
            subtitles?: Comment | null,
            countries?: Comment | null,
            description?: Comment | null,
            tags?: Comment | null,
            covers?: Comment | null,
            trailers?: Comment | null,
            rights_area?: Comment | null,
            rights_cat?: Comment | null,
            unit?: Comment | null,
            mmchannel: Comment | null,
            mmcertno: Comment | null,
            mmowner: Comment | null,
            licno: Comment | null,
            licowner: Comment | null,
            tvthemes: Comment | null,
            rowners: Comment | null,
            ssources: Comment | null,
            agerestriction: Comment | null,
            rsterritories: Comment | null,
            rsmedium: Comment | null,
            tvbrdcsterr: Comment | null,
            tvdienv: Comment | null,
        },
        checkmarks: {
            unit: CheckMark,
            title: CheckMark,
            qualities: CheckMark,
            collections: CheckMark,
            languages: CheckMark,
            subtitles: CheckMark,
            countries: CheckMark,
            description: CheckMark,
            tags: CheckMark,
            covers: CheckMark,
            trailers: CheckMark,
            rights_area: CheckMark,
            rights_cat: CheckMark,
            mmchannel: CheckMark,
            mmcertno: CheckMark,
            mmowner: CheckMark,
            licno: CheckMark,
            licowner: CheckMark,
            tvthemes: CheckMark,
            rowners: CheckMark,
            agerestriction: CheckMark,
            rsterritories: CheckMark,
            rsmedium: CheckMark,
            tvbrdcsterr: CheckMark,
            tvdienv: CheckMark,
            [key: string]: CheckMark,
        }
    },
    ctype: ContentType,
    rsterritories: { id: number, label: string, name: string }[], // rsterritory  объем прав - территория
    rsmedium: { id: number, label: string, name: string }[], // rsmedium объем прав - среда
    mm: number,
    tvbrdcsterr: { id: number, label: string, name: string }[], // tvbrcsterr - территория вещания
    tvdienv: { id: number, label: string, name: string }[], // tvdienv - среда распространения
    visible_to_guests: boolean,
    recommended: boolean,
    adm_visible_to_guests: boolean,
    adm_show_on_welcome: boolean,
    adm_promo_on_welcome: boolean,
    adm_priority: number,
    bplayer: BPlayer,
}

export enum CrmStatus {
    OK = 1, // (Green|OK)
    WARN = 2, //(Yellow|WARN)
    CRIT = 3, // (Red|CRIT)
    PROLONGED = 4,
}

export interface CrmConsumerRecord {
    provider: AccountListElement,
    consumer: AccountListElement,
    tvunits: { count: number | null, crm_status: CrmStatus | null },
    mediaunits: { count: number | null, crm_status: CrmStatus | null },
    products: { count: number | null, crm_status: CrmStatus | null },
}

export interface CrmConsumerRecordDetail {
    provider: number,
    consumer: number,
    unit?: {
        id: number,
        ctype: ContentType,
        title: string,
        category: { id: number, name: string } | null,
        covers: string[]
    },
    accesspermission: {
        id: number,
        status: number,
        issued: string,
        revoked: string | null,
        validity: Validity,
        params: {
            fmt: {
                id: number,
                name: string,
            }[],
            version: string
        },
        crm_status: CrmStatus,
        contracts: {
            id: number,
            number: string,
            validity: Validity,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            additional: any,
            cotype: 1,
            status: 2,
            concluded: string | null,
            terminated: string | null,
            crm_status: CrmStatus
        }[],
        sources: {
            id: number,
            name: string
        }[]
    },
    accessrequest: null
}

export interface CrmProductRecord {
    provider: AccountListElement,
    unit: {
        id: number,
        ctype: ContentType,
        title: string,
        category: { id: number, name: string } | null,
        covers: string[]
    },
    consumers: number,
    crm_status: CrmStatus
}

export interface CrmProductRecordDetail {
    provider: number,
    unit: number,
    consumer: AccountListElement,
    crm_status: CrmStatus
}

export interface DownloadInfo {
    id: number,
    created: string,
    file: MediaFile,
    downloaded: boolean,
    percent: number,
    avgrate: number,
    content: {
        id: number,
        title: string,
        title_eng: string,
        owner: {
            username: string,
            email: string,
            profile: Profile,
            id: number
        },
        covers: string[]
    }
}

export interface FileDescription {
    id: number,
    account_id: number,
    user_id: number,
    upload_uuid: string,
    cat: FSObjectCategory,
    state: FSObjectState,
    name: string,
    size: number,
    // cksums: {},
    uploaded: string,
    expire: null,
    url: string,
    signedurl: string,
}

export type HistoryAction = 'agreed' | 'comment' | 'comment_update' | 'comment_add' | 'comment_delete' | 'cover_upload' | 'cover_delete' | 'create' | 'change' | 'delete' | 'file_upload' | 'file_delete';

export interface History {
    id: number,
    action: HistoryAction,
    added: string,
    role: HISTORY_ROLE,
    username: string,
    usernickid: string,
    acname: string,
    acnickname: string | null,
    attr: CONTENT_FIELD,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ovalue: any | null, // JSON
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nvalue: any | null, // JSON
    hid: string,
    user: number,
    account: number,
    unit: number,
    ctype: number,
}

export interface MediaFile {
    id: number,
    name: string,
    size: number,
    url: string,
    downloaded: boolean,
    transcoded?: MediaFileTranscoded[],
    attributes: {
        orbox?: Orbox,
    },
    cksums: {
        md5: string,
        sha512: string,
    }
}

export interface MediaFileTranscoded {
    profile_id: number,
    size: number,
    url: string
}

export interface Orbox {
    passed: boolean,
    report: boolean,
    videocodec: {
        value: string,
        passed: boolean
    },
    resolution: {
        value: string,
        passed: boolean
    },
    check: {
        done: boolean,
        message: string
    }
}

export interface PermissionUnit {
    id: number,
    ctype: number,
    title: string,
    title_eng: string,
    category: {
        id: number,
        name: string
    },
    qualities: {
        id: number,
        name: string
    }[],
    covers: string[],
    rowners: number[],
    ssources: ContentTVSource[]
}

export interface ContractUnit {
    id: number,
    ctype: ContentType,
    title: string,
    title_eng: string,
    category: {
        id: number,
        name: string
    } | null,
    qualities: {
        id: number,
        name: string
    }[],
    covers: string[],
    ssources: ContentTVSource[]
    rowners: number[],
}

export enum MBDictionaryType {
    SYSTEM = 1,
    USER = 2,
}

export type MBDictionaryFieldType = 'boolean' | 'integer' | 'string' | 'float' | 'complex';

export interface MBDictionaryDescriptionTopField { // - Дополнительные поля для значений справочника
    label: string,                             // - Название поля в передаваемом json-е
    name: string,                              // - Название отображаемое
    type: MBDictionaryFieldType,               // - Тип поля: boolean,interger,string,float,complex(содержит вложенные поля, есть поле fields[])
    required: boolean,                         // - необходимо указывать или нет
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    default: any,                              // - значение по умолчанию
    null: boolean,                             // - возможно использовать null или нет
    source: string                             // - служебная информация для бека, где хранится значение этого поля в таблице бд
    many?: boolean,
    fields: MBDictionaryDescriptionTopField[]
}

export interface MBDictionaryDescription {
    label: string,              // - идентификатор
    typ: number,                // - числовой идентификатор
    name: string,               // - название
    description: string | null, // - описание
    type: MBDictionaryType,     // - тип справочника (битовое поле) 1 - Системный, 2 - Пользовательский
    many?: boolean,
    additional: { // - Дополнительная информация
        fields: MBDictionaryDescriptionTopField[]
    }
}

export interface MBDictionary {
    id: number,     // - id
    terms: {        // - названия на русском и английском
        ru: string,
        en: string
    },
    rank: number,   // - ранк
    // - То что ниже, сформировано на основе описаний приведенных выше
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
}

export interface MBDictionaryRequest {
    did: string     // - название словаря
    id: number,     // - id
    terms: {        // - названия на русском и английском
        ru: string,
        en: string
    },
    rank: number,   // - ранк
    // - То что ниже, сформировано на основе описаний приведенных выше
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
}

export type BlockLabel = 'welcome-news' | 'welcome-banners' | 'welcome-promo' | 'welcome-recommends'
    | 'welcome-polls' | 'welcome-advertisements' | 'welcome-media' | 'welcome-vertical-media' | 'welcome-tv'
    | 'welcome-products' | 'welcome-notifications';

export interface PublicBloks {
    id: number,
    label: BlockLabel,
    title: string,
    published: string,
    items: (News | Promo | Adverts | Polls | Banner | MediaProduct | NotificationUnit)[],
}

export interface FileContentInfo {
    mode: string // 'RGB',
    size: number,
    format: string // 'JPEG',
    uploaded: string // '2026-05-28T14:27:38.501Z',
    dimensions: [number, number],
    'content-type': string // 'image/jpeg'
}

export interface FileContent {
    file: string,
    info: FileContentInfo
}

export interface AdvBanner {
    id: number,
    title: string,
    utm: string,
    content: {
        ru: {
            'enabled': boolean,
            'link_url': string,
            'img_size1': FileContent,
            'img_size2': FileContent | null
        },
        en: {
            'enabled': boolean,
            'link_url': string,
            'img_size1': FileContent,
            'img_size2': FileContent | null
        }
    }
}

export interface AdvAdminBanner {
    id: number,
    title: string,
    utm: string,
    content: {
        ru: {
            enabled: boolean,
            link_url: string,
            img_size1: string,
            img_size2: string
        },
        en: {
            enabled: boolean,
            link_url: string,
            img_size1: string, // - ссылка на картинку (/cms/files/4eb7a26b-6c8c-4f99-8893-bf8f2d0bc0c9)
            img_size2: string
        }
    }
}

export interface Adverts {
    id: number,
    published: string,
    title: string,
    summary: string,
    body: string,
    tags: string[],
    start_at: string,
    finish_at: string,
    contacts: string,
}

export interface AdvertsAdmin {
    id: number,
    audience: CmsPageAvailability,
    pinned: number,
    priority: number,
    title: string,
    publish_at?: string | null,
    content: {
        ru: {
            enabled: boolean | null,
            title: string,
            summary: string,
            body: string,
            files: string[]
        },
        en: {
            enabled: boolean | null,
            title: string,
            summary: string,
            body: string,
            files: string[]
        }
    },
    start_at?: string | null,
    finish_at?: string | null,
    contacts: string,             // str, тектстовое описание от кого, т.к. непонятно кто там и какие объявления будет делать.
    tags: string[]
}

export interface CmsIndexBlock {
    id: number,
    ptype: number,
    max_blocks: number,
    allowed_blocks: CmsPageType[],
    title: string,
    items: { id: number, ptype: number, priority: number, title: string }[]
}

export interface CmsContentType {
    id: number,         //       - id контента
    ptype: number,      //       - тип
    priority: number,
    title: string,      //       - название
}

export type MediaType = 'tv' | 'media' | 'product';

export interface MediaUnit {
    id: number,
    title: string,
    agerestriction: string,
    timing: number,
    epnum: number,
    unit_type: string,
    genres: string[],
    qualities: string[],
    languages: string[],
    countries: string[],
    subtitles: string[],
    startyear: number | null,
    description: string,
    tags: string[],
    collections: string[],
    covers: string[],
    vertical: boolean,
    trailers: string[]
}

export interface NotificationUnit {
    id: number,
    published: string,
    start_at: string,
    finish_at: string,
    severity: Severity,
    icon: string,
    title: string,
    summary: string,
    body: string,
    metadata: {
        link_intro?: string,
        link_text?: string,
        link_url?: string,
    },
    files: [],
    tags: []
}

export interface AdminNotification {
    id: number,
    active: boolean,
    severity: Severity,
    icon: string,
    title: string,
    publish_at?: string,
    start_at?: string,
    finish_at?: string,
    urls: string[],
    content: {
        ru: {
            enabled: true,
            title: string,
            summary: string,
            body: string,
            files: string[],
            metadata: {
                link_intro?: string,
                link_text?: string,
                link_url?: string,
            }
        },
        en: {
            enabled: boolean,
            title: string,
            summary: string,
            body: string,
            files: string[],
            metadata: {
                link_intro?: string,
                link_text?: string,
                link_url?: string,
            }
        }
    },
    tags: string[]
}

export interface ProductUnit {
    id: number,
    name: string,
    territories: string[],
    types: string[],
    categories: string[],
    description: string,
    tags: string[],
    collections: string[],
    covers: string[],
    trailers: string[]
}

export interface TVUnit {
    id: number,
    title: string,
    agerestriction: string | null,
    tvqualities: string[],
    languages: string[],
    countries: string[],
    subtitles: string[],
    tvthemes: string[],
    category: string | null,
    tvbrdcsterr: string[],
    tvdienv: string[],
    description: string,
    tags: string[],
    collections: string[],
    covers: string[],
    trailers: string[],
    bplayer: {
        player: {
            url: string | null,
            type: number
        },
        enabled: boolean
    },
    mmchannel: string | null,
    mmcertno: string | null
}

export interface MediaProduct<T = TVUnit | MediaUnit | ProductUnit, K = MediaType> {
    id: number,
    published: string,
    type: K,
    product: T,
}

export interface News {
    id: number,
    published: string,
    priority: number,
    title: string,
    summary: string,
    body: string,
    files: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metadata: any,
    topics: {
        id: number,
        ctype: number,
        title: string,
        root: number | null
    }[],
    tags: string[],
    previous: string | null,
    next: string | null,
}

export interface NewsAdmin {
    id: number,
    audience: CmsPageAvailability,
    pinned: number,
    priority: number,
    title: string,
    publish_at?: string | null,
    urls?: string[],
    content: {
        ru: {
            enabled: boolean | null,
            title: string,
            summary: string,
            body: string,
            files: string[],
            metadata: {
                link_intro?: string,
                link_text?: string,
                link_url?: string,
            },
        },
        en: {
            enabled: boolean | null,
            title: string,
            summary: string,
            body: string,
            files: string[],
            metadata: {
                link_intro?: string,
                link_text?: string,
                link_url?: string,
            },
        }
    },
    topics: number[],
    tags: string[]
}

export interface NewsTopic {
    id: number,
    title: string,
    root: number,
}

export interface NewsAdminTopic {
    id: number,
    title: {
        en: string,
        ru: string
    },
    root: number | null
}

export interface Promo {
    id: number,
    published: string,
    start_at: string,
    finish_at: string,
    priority: number,
    title: string,
    summary: string,
    body: string,
    files: string[],
    tags: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metadata: any,
    promocodes: string[],       // str[] - список промокодов
}

export interface PromoAdmin {
    id: number,
    pinned: number,
    priority: number,
    title: string,
    publish_at?: string | null,
    start_at?: string | null,       //- datetime - дата начала действия
    finish_at?: string | null,      //- datetime - дата окончания действия
    urls: string[],
    content: {
        ru: {
            enabled: boolean | null,
            title: string,
            summary: string,
            body: string,
            files: string[],
            metadata: {
                link_intro?: string,
                link_text?: string,
                link_url?: string,
            },
        },
        en: {
            enabled: boolean | null,
            title: string,
            summary: string,
            body: string,
            files: string[],
            metadata: {
                link_intro?: string,
                link_text?: string,
                link_url?: string,
            },
        }
    },
    tags: string[]
    promocodes: string[],       // str[] - список промокодов
}

export enum CmsPageAvailability {
    PUBLIC = 1, // _("CONST-SYSTEM-000200", "Публичный")
    GUESTS = 2, // _("CONST-SYSTEM-000201", "Гостевой")
    PROVIDERS = 4, // _("CONST-SYSTEM-000202", "Поставщики")
    CONSUMERS = 8, // _("CONST-SYSTEM-000203", "Потребители")
    PARTNERS = 16,// _("CONST-SYSTEM-000204", "Партнеры")
    TVSRC_PARTNERS = 32,// _("CONST-SYSTEM-000205", "Партнеры-источники")
    ACCOUNTS = 64, // _("CONST-SYSTEM-000206", "Аккаунт")
    ALLACCOUNTS = 62,// _("CONST-SYSTEM-000220", "Все аккаунты")
    ALL = 65535,// _("CONST-SYSTEM-000221", "Все")
}

export enum CmsPollParticipants {
    ACCOUNT = 1, // _("CONST-SYSTEM-000350", "Аккаунты")
    USER = 2, // _("CONST-SYSTEM-000351", "Пользователи")
}

export enum CmsPollResultsVisibility {
    ALWAYS = 1, // _("CONST-SYSTEM-000300", "Показывать результаты сразу")
    AFTERPOLL = 2, // _("CONST-SYSTEM-000301", "Показывать результаты после голосования")
}

export enum CmsPageType {
    NEWS = 1, // _("CONST-SYSTEM-000100", "Новости")
    PROMOTIONS = 2, // _("CONST-SYSTEM-000101", "Акции")
    POLLS = 3, // _("CONST-SYSTEM-000102", "Голосования")
    BLOCKS = 4, // _("CONST-SYSTEM-000103", "Блоки")
    BANNERS = 5, // _("CONST-SYSTEM-000104", "Баннеры")
    PAGES = 6, // _("CONST-SYSTEM-000105", "Страницы")
    PAGEDINDEX = 7, // _("CONST-SYSTEM-000106", "Постраничный индекс")
    POLLOPTION = 8, // _("CONST-SYSTEM-000107", "Опция голосования")
    RECOMMENDS = 9, // _("CONST-SYSTEM-000108", "Рекомендуем")
    ADVT = 10, // _("CONST-SYSTEM-000109", "Объявления")
    PRODUCTS = 11, // _("CONST-SYSTEM-000110", "Услуги")
    MEDIA = 12, // _("CONST-SYSTEM-000111", "Видеоконтент")
    TV = 13, // _("CONST-SYSTEM-000112", "Телеканал")
    NTFY = 14, // _("CONST-SYSTEM-000113", "Уведомления")
}

export interface Polls {
    id: number,
    visibility: CmsPollResultsVisibility,
    participants: CmsPollParticipants,
    active: boolean,
    changevote: boolean,
    title: string,
    summary: string,
    body: string,
    published: string,
    start_at: string,
    finish_at: string,
    options: {
        id: number,
        pos: number,
        title: string,
        votes: number | null        // - кол-во голосов за эту опцию, null - значит недоступно для просмотра
    }[],
    vote: {                  // - Как проголосовал текущий пользователь/аккаунт
        option: number | null,        // - id опции, за которую голосовал
        voted_at: string | null,      // - когда
        account: number | null,       // - аккаунт
        user: number | null           // - пользователь
    }
}

export interface PollsOption {
    pos: number,                //int, номер позиции
    option: {
        ru: {
            enabled: boolean | null,    //bool, вкл/выкл для языка
            title: string         //str, название опции
        },
        en: {
            enabled: boolean | null,
            title: string
        }
    }
}

export interface PollsOptionFull extends PollsOption {
    id: number,
    votes: number,
}

export interface PollsAdmin {
    id: number,
    audience: CmsPageAvailability,
    pinned: number,
    priority: number,
    visibility: CmsPollResultsVisibility,   // int, видимость результатов
    participants: CmsPollParticipants,      // int, кто является участником голосования аккаунты/пользователи
    changevote: boolean,                    // bool, можно ли изменить свой выбор true-да/false-нет
    title: string,
    publish_at?: string | null,
    start_at?: string | null,
    finish_at?: string | null,
    content: {
        ru: {
            enabled: boolean | null,
            title: string,
            summary: string,
            body: string,
            files: number[]
        },
        en: {
            enabled: boolean | null,
            title: string,
            summary: string,
            body: string,
            files: number[]
        }
    },
    options: PollsOptionFull[],
}

export type PermissionMap = {
    [key in PERMISSIONS]: PermissionMapValue
}

export interface PermissionMapValue {
    users: { id?: number, username?: string, company?: string }[] | string[], groups: { id?: number, groupname?: string, company?: string }[] | string[]
}

export interface PortfolioRecord {
    consumer: AccountListElement,
    provider: AccountListElement,
    unit: {
        id: number,
        ctype: number,
        licno: string,
        owner: number,
        title: string,
        mmcertno: string | null,
        mmchannel: string,
        unit_type: {
            id: number | null,
            terms: string | null
        },
        tvcategory: {
            id: string | null,
            terms: string | null
        },
        covers: string[]
    },
    sources: { id: number, label: string, name: string }[],
    crm_status: CrmStatus,
}


export interface PortfolioRecordDetail {
    consumer: number,
    provider: number,
    unit: number,
    contracts: PortfolioRecordDetailContract[],
    accesspermission: PortfolioRecordDetailPermission,
    sources: {
        id: number,
        label: string,
        name: string
    }[],
    signals: {
        id: number,
        name: string,
        source: {
            id: number,
            label: string,
            name: string,
        },
        crm_status: CrmStatus
    }[]
}

export interface PortfolioRecordDetailContract {
    id: number,
    number: string,
    validity: Validity | null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additional: any,
    cotype: number,
    status: number,
    concluded: string | null,
    terminated: string | null,
}

export interface PortfolioRecordDetailPermission {
    id: number,
    status: number,
    issued: string,
    revoked: string | null,
    validity: Validity,
    params: {
        fmt: {
            id: number,
            name: string,
        }[],
        version: string
    },
    crm_status: CrmStatus
}

export interface ProductRequest {
    id?: number
    provider: number, // - Идентификатор аккаунта, прдеоставляющего продукт/услугу
    name: { ru: string, en: string, default: string } | string, // - Название продукта в json {"ru": "Название на русском", "en": "Название на аглицком", "default": "Название если нету разделения руский/нерусский" }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details: any,     // - json - какие то дополнительные условия, я не структурировал, т.к. нет у понимания что там может быть
    active: boolean,  // - активна/неактивна (на будущее)
    tariffoptions: number[], // - list[tariffs.options.id] , администраторы сайта могут привязать тарифную опцию как продукт
    // files: number[],  // - list[files.id] список привязанных файлов
    docs: number[],
    covers?: (string | number)[]
    trailers?: (string | number)[]
    tags: number[],
    collections: number[],
    categories: number[],
    territories: number[],
    types: number[],
    view: ACCESS_TYPES,
    view_accesslist: number[],
    offers: number[],
    visible_to_guests: boolean,
    card_prefix?: string,
}

export interface Product {
    id: number,
    provider: AccountListElement,
    label: string,
    name: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details: any,
    active: boolean,
    tariffoptions: {
        id: number,
        label: string,
        name: string,
        value: number,
        isfixed: boolean,
        price: string | null,
        group: TariffGroup
    }[],
    files: string[],
    offers: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        org: OrgMinimal,
        owner: UserProfileMinimal,
        logo: string | null
    }[],
    offered: boolean,
    new: boolean,
    ar_cnt: number,

    tags: { id: number, name: string }[],
    collections: { id: number, name: string }[],
    categories: { id: number, name: string }[],
    types: { id: number, name: string }[],
    territories: { id: number, name: string }[],
    covers: { id: number, name: string, size: number, url: string }[],
    visible_to_guests: boolean,
    recommended: boolean,
    adm_visible_to_guests: boolean,
    adm_show_on_welcome: boolean,
    adm_promo_on_welcome: boolean,
    adm_priority: number,
    card_prefix: string | null,
    system_prefixes: {
        card: string, // 'account14/Content/Media/Самый лучший фильм/',
        covers: string, // 'account14/Content/Media/Самый лучший фильм/Обложки/',
        trailers: string, // 'account14/Content/Media/Самый лучший фильм/Трейлеры/',
    },
}

export interface ProductFull extends Product {
    docs: { id: number, name: string, size: number, url: string }[],
    view: number,
    view_accesslist: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        org: OrgMinimal,
        owner: UserProfileMinimal,
        logo: string | null
    }[],
    trailers: { id: number, name: string, size: number, url: string, signedurl: string }[],
    accreq: { id: number, account_id: number, status: AccessRequestStatus, issuer_id: number, recipient_id: number }[],
}

export interface PublicContent {
    id: number,
    created: string,
    added_by: number,
    unit: number,
    title: string,
    expiry: string, // "2026-08-30T18:40:00+03:00",
    url: string, 
}

export interface PublicProduct {
    id: number,
    created: string,
    added_by: number,
    product: number,
    title: string,
    expiry: string, // "2026-08-30T18:40:00+03:00",
    url: string, 
}

export enum RegistryRecordType {
    TVPERMIT = 1, // _("CONST-010-4001", "Разрешение")
    CONTRACT = 2, // _("CONST-010-4002", "Договор")
}

export enum RegistryCombinedStatus {
    OK = 1, // _("CONST-010-5001", "OK")
    WARNING = 2, // _("CONST-010-5001", "Предупреждение")
    CRITICAL = 3, // _("CONST-010-5001", "Критический")
    PROLONGED = 4,
}

export interface RegistryRecord {
    id: number,
    rectype: RegistryRecordType,
    status: RegistryCombinedStatus,
    crm_status: CrmStatus,
    contract: Contract | null,
    permit: AccessPermission | null
}

export interface ReferenceBooks {
    genre: { id: number, rank: number, label: string, displayname: string }[],
    language: { id: number, rank: number, label: string, displayname: string }[],
    country: { id: number, rank: number, label: string, displayname: string }[],
    utype: { id: number, rank: number, label: string, displayname: string }[],
    collection: { id: number, rank: number, label: string, displayname: string }[],
    // rightsarea: { id: number, rank: number, label: string, displayname: string }[],
    // rightscat: { id: number, rank: number, label: string, displayname: string }[],
    quality: { id: number, rank: number, label: string, displayname: string }[]
    subtitle: { id: number, rank: number, label: string, displayname: string }[]
    tvtheme: { id: number, rank: number, label: string, displayname: string }[]
    tvcategory: { id: number, rank: number, label: string, displayname: string }[]
    agerestriction: { id: number, rank: number, label: string, displayname: string }[]
    rsterritory: { id: number, rank: number, label: string, displayname: string }[]
    rsmedium: { id: number, rank: number, label: string, displayname: string }[]
    tvbrcsterr: { id: number, rank: number, label: string, displayname: string }[]
    tvdienv: { id: number, rank: number, label: string, displayname: string }[]
    tvquality: { id: number, rank: number, label: string, displayname: string }[]
    tvsource: { id: number, rank: number, label: string, displayname: string }[],
    tvsat: { id: number, rank: number, label: string, displayname: string }[],
}

export interface ProductReferenceBooks {
    prcategory: { id: number, rank: number, label: string, displayname: string }[],
    prtype: { id: number, rank: number, label: string, displayname: string }[],
    rsterritory: { id: number, rank: number, label: string, displayname: string }[],
    collection: { id: number, rank: number, label: string, displayname: string }[],
}

export interface ProductUserBooks {
    tag: { id: number, rank: number, owner_id: number, name: string }[],
}

export interface ProductUsedBooks {
    prcategory: { id: number, rank: number, label: string, displayname: string }[],
    prtype: { id: number, rank: number, label: string, displayname: string }[],
    rsterritory: { id: number, rank: number, label: string, displayname: string }[],
    collection: { id: number, rank: number, label: string, displayname: string }[],
    tag: string[],
}

export interface PublicBanner {
    id: number,
    label: 'advertising-banners-1' | 'advertising-banners-2',
    title: string,
    utm?: string,
    link_url: string,
    img_size1: string,
    img_size2?: string,
}

export interface ExtendableListItem {
    id: number,
    sys: boolean,
    rank: number,
    title: {
        ru: string,
        en: string
    }
}

export interface TotalAccessStatus {
    all: number,
    opened: number,
    pending: number,
    granted: number,
    granted_downloaded: number,
    rejected: number,
    canceled: number
}

export interface TVDBChannel {
    id: number,
    brandname: string,
    rfbroadcasting: boolean,
    mmchannel: string,
    mmcertno: string,
    mmowner: AccountListElement,
    licno: string,
    licowner: AccountListElement,
    comment: string,
    added: string,
    updated: string,
}

export interface TVDBChannelRequest {
    id?: number,
    brandname: string,
    rfbroadcasting: boolean,
    mmchannel: string,
    mmcertno: string,
    mmowner: number | null,
    licno: string,
    licowner: number | null,
    comment: string
}

export interface TVDBKKAccess {
    id: number,
    tvsignal: {               //- информация о сигнале
        id: number,
        tvdbid: number,
        channel: number,
        source: number,
        name: string,
        visibility: boolean | null,
        quality: string,
        playlist_urls: string[],
        dst_ip: string,
        dst_port: string,
        src_ip: string,
        src_port: string,
        format: {
            label: string,
            terms: {
                en: string,
                ru: string
            }
        },
        details: {
            tvstream_id: number,
        }
    },
    signal_state: number,          //- статус сигнала из твдб
    rights_end_date: string | null,    //- права
    status: number,                //- подается (по версии медиабазы)
    consumer: number             //- id потребителя
}

export interface TVDBKKConsumer {
    consumer: AccountListElement,
    signals: number                       // - Количество сигналов
}

export interface TVDBKKConsumersDetail {
    mm: number,                       // - id из справочника телеканалов
    brandname: string,          // - brandname из справочника телеканалов
    mmchannel: string,          // - название по св-ву сми из справочника телеканалов
    signal: string,                    // - id сигнала
    name: string,              // - название сигнала
    status: number,                    // - 1-подается, в данных из твдб другого и нет
    signal_state: number | null,           // - статус сигнала из твдб для отображения: 0 - ОК / 1 - НЕ ОК
    rights_end_date: string | null         // - Срок окончания действия разрешения в твдб, дата, кстати и signal_state и эта дата могут быть null вроде
}

export interface TVDBKKSignal {
    id: number,                     //- id сигнала
    tvdbid: number,                 //- id сигнала из твдб
    channel: {                      //- Справочник телеканалов
        id: number,
        brandname: string,
        rfbroadcasting: boolean,
        mmchannel: string,
        mmcertno: string | null,
        mmowner: number,
        licno: string | null,
        licowner: number
    },
    source: number,                 //- источник из медиабазы
    name: string,                   //- название, ну и так далее, как в мониторинге
    visibility: number | null,
    quality: string,
    playlist_urls: string[] | null,
    dst_ip: string,
    dst_port: string,
    src_ip: string,
    src_port: string,
    format: {
        label: string,
        terms: {
            en: string,
            ru: string
        }
    } | null,
    details: {
        audio: {
            pid: number,
            freq: string,
            lang: string,
            type: 'audio',
            codec: string,
            bitrate: number,
            channels: 'stereo',
            codec_tag: string,
            codec_long: string
        }[],
        video: {
            dar: string,            // '16:9'
            pid: number,
            sar: string,            // '1:1',
            type: 'video',
            codec: string,          //- Отсюда берется кодес
            level: string,
            chroma: string,
            bitrate: number,
            profile: string,
            codec_tag: string,
            codec_long: string,
            frame_rate: number,
            resolution: string,     // '1920x1080',
            color_range: string,    // 'tv',
            has_b_frames: number,
        },
        subtitles: {
            pid: number,
            lang: string,
            type: 'subtitle',
            codec: string,
            bitrate: number,
            codec_tag: string,
            codec_long: string
        }[],
        inputs: {                   //- Это я так понимаю входные потоки основной (reserve:0) и резервный (reserve:1)
            dst_ip: string,
            reserve_status: number,
            dst_port: number,
            source_id: number,
            input_port_code: string,
            tvstream_id: number,
        }[],
        total_bitrate: number
    } | null,
    consumers_count: number,        //- Количество потребителей, потребляющих
    consumers: {                    //- Их список, ну поля вобщем то как и в предидущем вызове, consumer - стандартные данные аккаунта
        signal_state: number | null,
        rights_end_date: string | null,
        status: 1,
        consumer: AccountListElement
    }[],
}

export interface TVDBKKStatus {
    id: number,                     // - id сигнала
    tvdbid: number,                 // - вероятно это значение нужно будет в дальнейшем для вытаскивания графиков мониторинга
    channel: Omit<TVDBChannel, 'added' | 'updated' | 'comment'>,
    source: number,                 // - Источник в медиабазе
    name: string,                   // - Название сигнала
    visibility: number,             // - Я не использую, только храню: 1-Скрыт везде/2-Виден тольео вещателю/3-Виден в каталоге/4-Виден в избранном
    quality: string,                // - Качество как есть
    playlist_urls: string[] | null,
    dst_ip: string,
    dst_port: number,
    src_ip: string,
    src_port: number,
    format: {
        label: string,
        terms: {
            en: string,
            ru: string
        }
    },
    details?: {                      //- Разные странные поля хранятся в details, я их никак не верифицирую, получаю из tvdb и просто сохраняю
        audio: {
            pid: number,
            freq: string,
            lang: string,
            type: 'audio',
            codec: string,
            bitrate: number,
            channels: 'stereo',
            codec_tag: string,
            codec_long: string
        }[],
        video: {
            dar: string,            // '16:9'
            pid: number,
            sar: string,            // '1:1',
            type: 'video',
            codec: string,          //- Отсюда берется кодес
            level: string,
            chroma: string,
            bitrate: number,
            profile: string,
            codec_tag: string,
            codec_long: string,
            frame_rate: number,
            resolution: string,     // '1920x1080',
            color_range: string,    // 'tv',
            has_b_frames: number,
        },
        inputs: {                   //- Это я так понимаю входные потоки основной (reserve:0) и резервный (reserve:1)
            dst_ip: string,
            reserve: number,
            dst_port: number,
            source_id: number,
            input_port_code: string
            tvstream_id: number,
        }[],
        source_id: number,
        tvstream_id: number,
        subtitles: {
            pid: number,
            lang: string,
            type: 'subtitle',
            codec: string,
            bitrate: number,
            codec_tag: string,
            codec_long: string
        }[],
        total_bitrate: number,                       //- Вроде как битрейт
        hls_playlist_proxy_urls: string[]
    }
}

export interface TVSignal {
    id: number,
    owner: AccountListElement,
    tvchannel: {
        id: number,
        brandname: string,
        rfbroadcasting: boolean,
        mmchannel: string,
        mmcertno: string,
        mmowner: AccountListElement,
        licno: string | null,
        licowner: AccountListElement,
        comment: string | null,
        added: string,
        updated: string,
    },
    source: {
        id: number,
        label: string,
        name: string,
        param: {
            stype: SourcesType,
        }
    },
    quality: {
        id: number,
        label: string,
        name: string
    },
    name: string,
    description: string | null
}

export interface TVSignalRequest {
    id?: number,
    owner?: number,
    tvchannel?: number
    source?: number,
    quality?: number,
    name?: string,
    description?: string
}

export enum TVSignalStateStatus {
    SERVED = 1,
    NOT_SERVED = 2,
}

export interface TVSignalState {
    id: number,
    ctvsignal: {
        id: number,
        name: string,
        owner: AccountListElement,
        source: {
            id: number,
            label: string,
            name: string
        },
        quality: {
            id: number,
            label: string,
            name: string
        },
        tvchannel: number
    },
    client: AccountListElement,
    state: TVSignalStateStatus
}

export interface TVSignalStateRequest {
    id?: number,
    ctvsignal: number,
    client: number,
    state: TVSignalStateStatus
}

export interface UploadRequest {
    uuid: string,
    account_id?: number,
    name: string,
    expire?: string,
    version?: string,
    cat: FSObjectCategory,
    file: File,
    onProgress?: (e: AxiosProgressEvent) => void,
}

export interface UploadMultipartRequest {
    uuid: string,
    account_id?: number,
    cat: FSObjectCategory,
    chunkIndex: number,
    chunkCount: number,
    fullsize: number,
    crc: string,
    chunk_size: number,
    orig_name: string,
    data: Buffer,
    signal?: AbortSignal
}

export interface UsedBooks extends Omit<ReferenceBooks, 'tvsat'> {
    years: number[],
    tag: string[],
    rightsarea: string[],
    rightscat: string[],
    tvsat: { id: number, rank: number, owner_id: number | null, name: string }[],
    timingrange: { id: number, rank: number, label: number, displayname: string }[]
}

export interface UserBooks extends Omit<ReferenceBooks, 'tvsat'> {
    rightsarea: { id: number, rank: number, owner_id: number, name: string }[],
    rightscat: { id: number, rank: number, owner_id: number, name: string }[],
    years: number[],
    tag: { id: number, rank: number, owner_id: number | null, name: string }[],
    tvsat: { id: number, rank: number, owner_id: number | null, name: string }[],
}

export type UserBooksType = 'genre' | 'language' | 'country' | 'quality' | 'utype' | 'subtitle' | 'tvtheme' | 'tag' | 'collection' | 'rightsarea' | 'rightscat' | 'tvsat' | 'tvcategory';

export interface UserBooksRequest {
    id?: number,
    typ: UserBooksType
    name: string
    rank: number
}

export interface UserBooksResponse {
    id: number,
    typ: UserBooksType,
    owner_id: number,
    name: string,
    rank: number
}

export interface UploadAccount {
    id: number,
    rtype: number,
    name: string,
    nickname: string | null,
    orgname: string | null,
    logo: string | null
}

export interface Upload {
    uuid: string,
    utid: number,
    owner: UploadAccount,
    account: UploadAccount,
    user: {
        id: number,
        nickid: string
    },
    unit_id: number | null,
    name: string,
    created: string,
    updated: string,
    state: UploadCodes,
    total_size: number,
    total_chunks: number,
    uploaded_chunks_size: number,
    uploaded_chunks_count: number,
    cat: FSObjectCategory,
    msg: string | null,
    expire: | null,
}

export interface UploadFull {
    utid: number,
    uuid: string,
    unit_id: null,
    user_id: number,
    account_id: number,
    owner_id: number,
    cat: FSObjectCategory,
    state: xUploadState,
    msg: string | null,
    created: string,
    updated: string,
    expire: string | null,
    orig_name: string,
    total_size: number,
    total_chunks: number,
    uploaded_chunks: number,
    uploaded_size: number,
    missed_chunks: number[][]
}

export interface UploadShort {
    id: number,
    file_id: number,
    utid: number,
    uuid: string,
    unit_id: number | null,
    user_id: number,
    account_id: number,
    owner_id: number,
    cat: FSObjectCategory,
    state: UploadCodes,
    msg: string | null,
    created: string,
    updated: string,
    expire: string | null,
    orig_name: string,
    total_size: number,
    total_chunks: number,
    uploaded_chunks: number,
    uploaded_size: number,
    missed_chunks: number[][]
}

export interface UploadResponse {
    src?: File
    uuid: string,
    unit_id: number,
    content?: {
        id: number,
        ctype: number,
        title: string,
        title_eng: string,
        owner: Profile,
        covers: string[]
    }
    name: string,
    created?: string,
    updated?: string,
    state: UploadCodes,
    total_size: number,
    total_chunks: number,
    uploaded_chunks_size: number,
    uploaded_chunks_count: number,
    missed_chunks?: number[][],
    cat?: number,

    utid?: number,
    msg?: string | null,
    account?: {
        id: number,
        rtype: number,
        name: string,
        nickname: string | null,
        orgname: string,
        logo: string
    },
    user?: {
        id: number,
        nickid: string
    }
}

export interface UploadUnitResponse extends UploadResponse {
    orig_name: string,
    missed_chunks: number[][],
    uploaded_size: number,
    uploaded_chunks: number,
    progress: number
}

export interface UnitUpload extends UploadResponse {
    url?: string
    src?: File
    progress: number
    errors: number
    uploadProcesser?: UploadProcesser
    missed_chunks: number[][],
}

export enum OperationMessageCode {
    START = 'SYSTEM-OP-001001',
    END = 'SYSTEM-OP-001002',
    INFO = 'SYSTEM-OP-001003',
    ERROR = 'SYSTEM-OP-001004',
}

export interface Operation {
    id: string,
    metadata: OperationMetadata
}

export interface OperationFull extends Operation {
    status: SdnOperationTaskStatus,
    messages: {
        code: OperationMessageCode,
        msg: string,
        text: string | null
        errors: string | null
    }[]
}

export interface OperationMetadata {
    command: string, // 'generate-static',
    parameters: {
        type: 'all' | 'only-news' | 'except-news'
    },
    module: string, // 'system.cms.static.generation.GenerateSite',
    ua: number,
    queue: string
}

const staggeredBaseQuery = retry(baseQueryWithReauth, {
    maxRetries: 0,
})

export const contentApiV1 = createApi({
    reducerPath: 'contentApiV1',
    baseQuery: staggeredBaseQuery,
    tagTypes: ['Content', 'PublicContent', 'Upload', 'Access', 'Media', 'Perm', 'Tvdb', 'TvdbKK', 'Crm', 'Dict', 'CMSNews', 'CMSPromo', 'CMSPolls', 'CMSAdverts', 'CMSNotify', 'CMSBanners', 'CMSIndex'],
    keepUnusedDataFor: 30,
    endpoints: (builder) => ({
        getContentList: builder.query<StatusPagesResponse<ContentUnit | ContentTVUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
            providesTags: () => [{ type: 'Content' }],
        }),
        getContentUnitList: builder.query<StatusPagesResponse<ContentUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/media/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
            providesTags: () => [{ type: 'Content' }],
        }),
        getContentUnitListCount: builder.query<StatusResponse<{ count: number }>, string>({
            query: (search) => `be/api/v1/content/media/count/?search=${search}`,
            providesTags: () => [{ type: 'Content' }],
        }),
        getContentOffers: builder.query<StatusPagesResponse<ContentUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/offers/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            providesTags: () => [{ type: 'Content' }],
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
        }),
        getOffersMedia: builder.query<StatusPagesResponse<ContentUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/media/offers/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            providesTags: () => [{ type: 'Content' }],
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
        }),
        getNewContent: builder.query<StatusPagesResponse<ContentUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/new/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     currentCache.results.next = newItems.results.next;
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
            providesTags: () => [{ type: 'Content' }],
        }),
        getNewMedia: builder.query<StatusPagesResponse<ContentUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/media/new/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     currentCache.results.next = newItems.results.next;
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
            providesTags: () => [{ type: 'Content' }],
        }),
        getCheckedContent: builder.query<StatusPagesResponse<ContentUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/media/moderated/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     currentCache.results.next = newItems.results.next;
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
            providesTags: () => [{ type: 'Content' }],
        }),
        getUserBook: builder.query<StatusResponse<UserBooks>, void>({
            query: () => 'be/api/v1/content/user_books/',
            // providesTags: () => [{ type: 'Content' }],
        }),
        getContentUserBook: builder.query<StatusResponse<UserBooks>, number>({
            query: (id) => `be/api/v1/content/${id}/user_books/`,
            // providesTags: () => [{ type: 'Content' }],
        }),
        getUsedBook: builder.query<StatusResponse<UsedBooks>, void>({
            query: () => 'be/api/v1/content/used_books/',
            // providesTags: () => [{ type: 'Content' }],
        }),
        getTVUsedBook: builder.query<StatusResponse<UsedBooks>, void>({
            query: () => 'be/api/v1/content/tv/used_books/',
            // providesTags: () => [{ type: 'Content' }],
        }),
        getMediaUsedBook: builder.query<StatusResponse<UsedBooks>, void>({
            query: () => 'be/api/v1/content/media/used_books/',
            // providesTags: () => [{ type: 'Content' }],
        }),
        getReferenceBook: builder.query<StatusResponse<ReferenceBooks>, void>({
            query: () => 'be/api/v1/content/reference_books/',
            // providesTags: () => [{ type: 'Content' }],
        }),
        createUserBooksItem: builder.mutation<StatusResponse<UserBooksResponse[]>, UserBooksRequest>({
            query: (data) => ({
                url: 'be/api/v1/content/manage/userbooks/',
                method: 'POST',
                body: data
            })
        }),
        createContentUserBooksItem: builder.mutation<StatusResponse<UserBooksResponse[]>, { content_id: number, data: UserBooksRequest }>({
            query: ({ content_id, data }) => ({
                url: `be/api/v1/content/${content_id}/manage/userbooks/`,
                method: 'POST',
                body: data
            })
        }),
        deleteUserBooksItem: builder.mutation<StatusResponse<null>, number>({
            query: (tag_id) => ({
                url: `be/api/v1/content/manage/userbooks/${tag_id}/`,
                method: 'DELETE',
            })
        }),
        deleteContentUserBooksItem: builder.mutation<StatusResponse<null>, { content_id: number, item_id: UserBooksRequest }>({
            query: ({ content_id, item_id: tag_id }) => ({
                url: `be/api/v1/content/${content_id}/manage/userbooks/${tag_id}/`,
                method: 'DELETE',
            })
        }),
        getContentUnitCollections: builder.query<StatusPagesResponse<{ id: number, name: string }>, number>({
            query: (id: number) => `be/api/v1/content/${id}/collections/`,
            // providesTags: (result, error, id) => [{ type: 'Content', id }],
        }),
        getContentUnit: builder.query<StatusResponse<ContentUnitFull | ContentTVUnitFull>, number>({
            query: (id: number) => `be/api/v1/content/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Content', id }],
        }),
        getMediaUnit: builder.query<StatusResponse<ContentUnitFull>, number>({
            query: (id: number) => `be/api/v1/content/media/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Content', id }],
        }),

        // История изменений единицы контента
        getContentHistory: builder.query<StatusPagesResponse<History>, number>({
            query: (id: number) => `/be/api/v1/content/${id}/history/`,
        }),
        // /be/api/v1/content/<id>/media/<mediaid>/attributes/orbox/passed/  - [GET] Результаты выполнения тестов Orbox
        getOrboxPassed: builder.query<StatusPagesResponse<History>, { id: number, mediaid: number }>({
            query: (data) => `/be/api/v1/content/${data.id}/media/${data.mediaid}/attributes/orbox/passed/`,
        }),
        // /be/api/v1/content/<id>/media/<mediaid>/attributes/orbox/report/  - [GET] Отчет Orbox
        getOrboxReport: builder.query<StatusPagesResponse<History>, { id: number, mediaid: number }>({
            query: (data) => `/be/api/v1/content/${data.id}/media/${data.mediaid}/attributes/orbox/report/`,
        }),
        // # Модерирование
        // /be/api/v1/content/<id>/moderation/comments/           - [CUD] Создание/удаление/изменение/список комментарием для единицы контента
        getContentComents: builder.query<StatusPagesResponse<Comments>, number>({
            query: (id: number) => `/be/api/v1/content/${id}/moderation/comments/`,
        }),
        // /be/api/v1/content/<id>/moderation/comments/fields     - Список доступных для комментирования полей
        getContentComentsFields: builder.query<StatusResponse<string[]>, number>({
            query: (id: number) => `/be/api/v1/content/${id}/moderation/comments/fields/`,
        }),
        // /be/api/v1/content/<id>/moderation/checkmarks/         - [CU] Список/редактирование галочек модерирования единицы контента
        getContentCheckmarks: builder.query<StatusResponse<{ [key: string]: CheckMark }>, number>({
            query: (id: number) => `/be/api/v1/content/${id}/moderation/checkmarks/`,
        }),
        setContentCheckmarks: builder.mutation<void, { id: number, checks: { [key: string]: boolean } }>({
            query: (data) => ({
                url: `/be/api/v1/content/${data.id}/moderation/checkmarks/`,
                method: 'POST',
                body: data.checks,
            })
        }),
        // /be/api/v1/content/<id>/moderation/checkmarks/fields/  - Список доступных для модерирования полей
        getContentCheckmarksFields: builder.query<StatusResponse<string[]>, number>({
            query: (id: number) => `/be/api/v1/content/${id}/moderation/checkmarks/fields/`,
        }),
        sendContent: builder.mutation<void, { id: number, accounts: string[] }>({
            query: ({ id, accounts }) => ({
                url: `be/api/v1/content/${id}/send/`,
                method: 'POST',
                body: { accounts }
            }),
        }),
        getContentUnitMedia: builder.query<StatusResponse<Pick<ContentUnitFull, 'mediafiles' | 'version' | 'id'>>, number>({
            query: (id: number) => `be/api/v1/content/${id}/`,
            transformResponse: (response: StatusResponse<Pick<ContentUnitFull, 'mediafiles' | 'version' | 'id'>>, _meta, _arg) => {
                const onlyMedia = response.results?.mediafiles;
                const version = response.results?.version;
                const id = response.results?.id;
                const result = { status: response.status, results: { mediafiles: onlyMedia, version, id } };
                return result;
            },
            providesTags: (result, error, id) => [{ type: 'Media', id }],
        }),

        getTVList: builder.query<StatusPagesResponse<ContentTVUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/tv/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
            providesTags: () => [{ type: 'Content' }],
        }),
        getContentTVListCount: builder.query<StatusResponse<{ count: number }>, string>({
            query: (search) => `be/api/v1/content/tv/count/?search=${search}`,
            providesTags: () => [{ type: 'Content' }],
        }),
        getOffersTV: builder.query<StatusPagesResponse<ContentTVUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/tv/offers/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            providesTags: () => [{ type: 'Content' }],
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
        }),
        getNewTV: builder.query<StatusPagesResponse<ContentTVUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/tv/new/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     currentCache.results.next = newItems.results.next;
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
            providesTags: () => [{ type: 'Content' }],
        }),
        getCheckedTV: builder.query<StatusPagesResponse<ContentTVUnit>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/tv/moderated/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     currentCache.results.next = newItems.results.next;
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
            providesTags: () => [{ type: 'Content' }],
        }),
        getTVUnit: builder.query<StatusResponse<ContentTVUnitFull>, number>({
            query: (id: number) => `be/api/v1/content/tv/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Content', id }],
        }),
        createTVUnit: builder.mutation<StatusResponse<ContentTVUnit>, Pick<ContentTVRequest, 'title' | 'languages' | 'subtitles' | 'countries'>>({
            query: (data: ContentTVRequest) => ({
                url: 'be/api/v1/content/tv/',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Content' }]
        }),
        createDraftTVUnit: builder.mutation<StatusResponse<ContentTVUnit>, { title?: string, owner: number }>({
            query: ({ title, owner }) => ({
                url: 'be/api/v1/content/tv/',
                method: 'POST',
                body: {
                    title: title || '',
                    title_eng: '',
                    languages: [],
                    subtitles: [],
                    countries: [],
                    description: '',
                    techinfo: '',
                    tags: [],
                    links: {},
                    version: 0,
                    acl: [],
                    view_access: ACCESS_TYPES.NOBODY,
                    view_accesslist: [],
                    download_access: ACCESS_TYPES.NOBODY,
                    download_accesslist: [],
                    typ: TYPS.DRAFT,
                    owner: owner
                }
            }),
        }),
        editTVUnit: builder.mutation<StatusResponse<ContentTVUnitFull>, Partial<ContentTVRequest>>({
            query: (data: ContentTVRequest) => ({
                url: `be/api/v1/content/tv/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Content' }, { type: 'Content', id: result?.results.id }]
        }),
        deleteTVUnit: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `be/api/v1/content/tv/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Content' }]
        }),

        createContentUnit: builder.mutation<StatusResponse<ContentUnitFull>, Pick<ContentRequest, 'title' | 'genres' | 'languages' | 'subtitles' | 'countries'>>({
            query: (data: ContentRequest) => ({
                url: 'be/api/v1/content/media/',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'Content' }]
        }),
        updateContentCms: builder.mutation<StatusResponse<ContentCms>, Partial<ContentCms>>({
            query: (data) => ({
                url: `/be/api/v1/content/${data.id}/cms/`,
                method: 'POST',
                body: data,
            }),
            // invalidatesTags: (result, error, { id }) => [{ type: 'Perm' }, { type: 'Perm', id: result?.results.id }]
        }),
        createDraftUnit: builder.mutation<StatusResponse<ContentUnitFull>, { title?: string, owner: number }>({
            query: ({ title, owner }) => ({
                url: 'be/api/v1/content/media/',
                method: 'POST',
                body: {
                    title: title || '',
                    title_eng: '',
                    languages: [],
                    subtitles: [],
                    countries: [],
                    description: '',
                    techinfo: '',
                    tags: [],
                    links: {},
                    version: 0,
                    acl: [],
                    view_access: ACCESS_TYPES.NOBODY,
                    view_accesslist: [],
                    download_access: ACCESS_TYPES.NOBODY,
                    download_accesslist: [],
                    typ: TYPS.DRAFT,
                    owner: owner
                }
            }),
        }),
        makeCensoredUnit: builder.mutation<StatusResponse<ContentUnitFull>, number>({
            query: (id: number) => ({
                url: `be/api/v1/content/${id}/`,
                method: 'PATCH',
                body: {
                    typ: TYPS.CENSORED,
                }
            }),
        }),
        unmakeCensoredUnit: builder.mutation<StatusResponse<ContentUnitFull>, number>({
            query: (id: number) => ({
                url: `be/api/v1/content/${id}/`,
                method: 'PATCH',
                body: {
                    typ: TYPS.NORMAL,
                }
            }),
        }),
        editContentUnit: builder.mutation<StatusResponse<ContentUnitFull>, Partial<ContentRequest>>({
            query: (data: ContentRequest) => ({
                url: `be/api/v1/content/media/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Content' }, { type: 'Content', id: result?.results.id }]
        }),
        deleteContentUnit: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `be/api/v1/content/media/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Content' }]
        }),
        getUploads: builder.query<StatusResponse<{ data: UploadResponse[] }>, void>({
            query: () => 'be/api/v1/content/uploads/',
            providesTags: (result, error, id) => [{ type: 'Content' }, { type: 'Upload' }],
        }),
        getUnitUploads: builder.query<StatusResponse<{ data: UploadResponse[] }>, number>({
            query: (id: number) => `be/api/v1/content/${id}/upload/?cat=1`,
            providesTags: (result, error, id) => [{ type: 'Content' }, { type: 'Upload', id }],
            transformResponse: (response: StatusResponse<{ data: UploadResponse[] }>, meta, arg) => {
                return { status: response.status, results: { data: response.results.data.filter(u => u.cat === 1) } };
            },
        }),
        getUnitTrailerUploads: builder.query<StatusResponse<{ data: UploadResponse[] }>, number>({
            query: (id: number) => `be/api/v1/content/${id}/upload/?cat=3`,
            transformResponse: (response: StatusResponse<{ data: UploadResponse[] }>, meta, arg) => {
                return { status: response.status, results: { data: response.results.data.filter(u => u.cat === 3) } };
            },
        }),
        getUnitUpload: builder.query<StatusResponse<UploadUnitResponse>, { id: number, uuid: string }>({
            query: (args) => `be/api/v1/content/${args.id}/upload/${args.uuid}/`,
            providesTags: (result, error, args) => [{ type: 'Content' }, { type: 'Upload', id: args.id }],
            transformResponse: (response: StatusResponse<UploadUnitResponse>, meta, arg) => {
                const result = { ...response.results, progress: response.results.uploaded_chunks_count / response.results.total_chunks * 100 };
                return { status: response.status, results: result };
            },
        }),
        deleteUnitUpload: builder.mutation<StatusResponse<UploadUnitResponse>, { id: number, uuid: string }>({
            query: (args) => ({
                url: `be/api/v1/content/${args.id}/upload/${args.uuid}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Upload' }]
        }),
        getMediaHeader: builder.mutation<void, string>({
            query: (url) => ({
                url: url,
                method: 'HEAD',
                credentials: 'include',
            }),
        }),
        uploadContentMediaChunk: builder.mutation<StatusResponse<UploadResponse>, { id: number, url: string, data: Buffer, signal?: AbortSignal }>({
            query: (args) => ({
                url: args.url,
                method: 'POST',
                body: args.data,
                headers: { 'Content-Type': 'application/octet-stream' },
                signal: args.signal,
            }),
        }),
        deleteContentMedia: builder.mutation<void, string>({
            query: (url) => ({
                url: url,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Media' }]
        }),
        createContentTrailer: builder.mutation<void, { id: number, file: File, onProgress?: (e: AxiosProgressEvent) => void }>({
            query(data) {
                const formData = new FormData();
                formData.append('file', data.file);
                return {
                    url: `be/api/v1/content/${data.id}/trailer/`,
                    method: 'POST',
                    body: formData,
                    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                        data.onProgress && data.onProgress(progressEvent)
                    }
                };
            },
        }),
        deleteContentTrailer: builder.mutation<void, string>({
            query: (url) => ({
                url: url,
                method: 'DELETE',
            }),
        }),
        createContentCover: builder.mutation<void, { id: number, file: File, onProgress?: (e: AxiosProgressEvent) => void }>({
            query(data) {
                const formData = new FormData();
                formData.append('file', data.file);
                return {
                    url: `be/api/v1/content/${data.id}/cover/`,
                    method: 'POST',
                    body: formData,
                    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                        data.onProgress && data.onProgress(progressEvent)
                    },
                };
            },
        }),
        deleteContentCover: builder.mutation<void, string>({
            query: (url) => ({
                url: url,
                method: 'DELETE',
            }),
        }),
        getMetaData: builder.mutation<string, { taskid?: string, id: number }>({
            query: (data) => ({
                url: `be/api/v1/content/${data.id}/metadata/`,
                method: 'POST',
                body: { taskid: data.taskid, inline: false },
                responseType: 'blob',
                responseDataHandler: async (blobData: BlobPart) => {
                    const blob = new Blob([blobData]);
                    return URL.createObjectURL(blob);
                },
            }),
        }),
        getDowloadInfo: builder.query<StatusResponse<DownloadInfo[]>, void>({
            query: () => 'be/api/v1/content/downloads/',
            providesTags: () => [{ type: 'Content' }],
        }),
        getContentDownloadInfo: builder.query<StatusResponse<DownloadInfo[]>, number>({
            query: (id: number) => `be/api/v1/content/${id}/downloads/`,
            providesTags: (result, error, id) => [{ type: 'Content', id }],
        }),
        accessRequestTV: builder.mutation<StatusResponse<AccessResponse>, {
            units: number[],
            sender: number,
            details: {
                licagr: number | null,
                pstype?: number | null,
                psdetails?: string,
                additional: string,
                ontest: number | null,
                // promcod?: string,
                promocodes: string[],
                sources?: { stype: SourcesType, params?: string, sources: /*{ id: number, name: string, info?: { org?: string } }*/number[], }[],
            },
            contracts?: number[],
            message?: string
        }>({
            query: ({ units, sender, details, message }) => ({
                url: 'be/api/v1/content/access/requests/',
                method: 'POST',
                body: { units: units, sender: sender, details: details, message: message || 'Empty message' }
            }),
            invalidatesTags: (result, error, data) => [{ type: 'Access' }, { type: 'Content' }]
        }),
        accessRequestUnit: builder.mutation<StatusResponse<AccessResponse>, { unit: number, sender: number, message?: string }>({
            query: (data) => ({
                url: 'be/api/content/access/requests/',
                method: 'POST',
                body: { units: [data.unit], sender: data.sender, message: data.message || 'Empty message' }
            }),
            invalidatesTags: (result, error, data) => [{ type: 'Access' }, { type: 'Content' }, { type: 'Content', id: data.unit }]
        }),
        accessRequestProduct: builder.mutation<StatusResponse<{
            group: number,
            count: number,
            requests: number[],
        }>, {
            products: number[], //- id услуг
            sender: number, // - id того для кого заяка
            details: {
                units: number[], // - в случае, если услугу нужно будет заказать для карточки (например из самой карточки) сюда id юнитов можно указывать
            },
        }>({
            query: ({ products, sender, details }) => ({
                url: 'be/api/v1/content/access/requests/products/',
                method: 'POST',
                body: { products, sender: sender, details: details }
            }),
            invalidatesTags: (result, error, data) => [{ type: 'Access' }, { type: 'Content' }]
        }),
        getAccessRequest: builder.query<StatusResponse<AccessFull>, { ar_id: number, acc_id: number }>({
            query: ({ ar_id, acc_id }) => `be/api/v1/content/access/requests/${ar_id}/${acc_id}/`,
            providesTags: (result, error, params) => [{ type: 'Access', id: params.ar_id }],
        }),
        getAccessRequestMessage: builder.query<StatusResponse<AccessMessage[]>, { ar_id: number, acc_id: number }>({
            query: ({ ar_id, acc_id }) => `be/api/v1/content/access/requests/${ar_id}/${acc_id}/message/`,
            providesTags: (result, error, params) => [{ type: 'Access', id: params.ar_id }],
        }),
        getAccessRequestMessageRecipients: builder.query<StatusResponse<AccountListElement[]>, { ar_id: number, acc_id: number }>({
            query: ({ ar_id, acc_id }) => `be/api/v1/content/access/requests/${ar_id}/${acc_id}/message/recipients/`,
            providesTags: (result, error, params) => [{ type: 'Access', id: params.ar_id }],
        }),
        getAccessRequestAllowedRecipients: builder.query<StatusResponse<AccountListElement[]>, { ar_id: number, acc_id: number }>({
            // Список аккаунтов, которым можно переслать заявку
            query: ({ ar_id, acc_id }) => `be/api/v1/content/access/requests/${ar_id}/${acc_id}/allowed/recipients/`,
            providesTags: (result, error, params) => [{ type: 'Access', id: params.ar_id }],
        }),
        getAccessRequestHistory: builder.query<StatusResponse<AccessHistory[]>, { ar_id: number, acc_id: number }>({
            // История заявки
            query: ({ ar_id, acc_id }) => `be/api/v1/content/access/requests/${ar_id}/${acc_id}/history/`,
            providesTags: (result, error, params) => [{ type: 'Access', id: params.ar_id }],
        }),
        getAccessRequests: builder.query<StatusPagesResponse<AccessResponse> & { results: { totals: TotalAccessStatus } }, OffsetLimit>({
            query: (params: OffsetLimit) => `be/api/v1/content/access/requests/?${Object.keys(params).filter(k => params[k] || params[k] === 0).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: (result, error, id) => [{ type: 'Access' }],
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getAccessRequestsSummary: builder.query<StatusResponse<AccessSummaryResponse>, void>({
            query: () => 'be/api/v1/content/access/requests/summary/',
            providesTags: (result, error, id) => [{ type: 'Access' }],
        }),
        accessRequestsSetStatus: builder.mutation<StatusResponse<AccessResponse>, { ar_id: number, acc_id: number, status?: AccessRequestStatus, sendto?: number, updated?: string, comment?: string, task?: AccessRequestTask, contracts?: number[] }>({
            query: ({ ar_id, acc_id, status, sendto, comment, contracts, updated, task }) => ({
                url: `be/api/v1/content/access/requests/${ar_id}/${acc_id}/`,
                method: 'PUT',
                body: {
                    status,
                    sendto,
                    updated,
                    comment,
                    task,
                    contracts,
                }
            }),
            invalidatesTags: (result, error, p) => [{ type: 'Access' }, { type: 'Access', id: p.ar_id }]
        }),
        getAccessRequestsAccounts: builder.query<StatusResponse<Omit<AccountShort, 'logo'>[]>, void>({
            query: () => 'be/api/v1/content/access/requests/accounts/',
        }),

        getCover: builder.mutation<string, string>({
            query: (url) => ({
                url: url,
                responseType: 'blob',
                responseDataHandler: async (data: BlobPart) => {
                    const blob = new Blob([data], { type: 'image/jpg' })
                    return URL.createObjectURL(blob);
                },
                cache: 'no-cache',
            }),
        }),
        getAccessPermissions: builder.query<StatusPagesResponse<AccessPermission>, OffsetLimit>({
            query: (params: OffsetLimit) => `be/api/v1/content/access/permissions/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: (result, error, id) => [{ type: 'Perm' }],
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getAccessPermission: builder.query<StatusResponse<AccessPermissionFull>, number>({
            query: (perm_id) => `be/api/v1/content/access/permissions/${perm_id}/`,
            providesTags: (result, error, id) => [{ type: 'Perm' }],
        }),
        getAccessPermissionPdf: builder.query<string, number>({
            query: (perm_id) => ({
                url: `be/api/v1/content/access/permissions/${perm_id}/pdf/`,
                responseType: 'blob',
                responseDataHandler: async (data: BlobPart) => {
                    const blob = new Blob([data], { type: 'application/pdf' })
                    return URL.createObjectURL(blob);
                },
                cache: 'no-cache',
            }),
        }),
        getAccessPermissionDocx: builder.query<string, number>({
            query: (perm_id) => ({
                url: `be/api/v1/content/access/permissions/${perm_id}/docx/`,
                responseType: 'blob',
                responseDataHandler: async (data: BlobPart) => {
                    const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
                    return URL.createObjectURL(blob);
                },
                cache: 'no-cache',
            }),
        }),
        createAccessPermission: builder.mutation<StatusResponse<AccessPermission>, AccessPermissionRequest>({
            query: (data) => ({
                url: 'be/api/v1/content/access/permissions/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Perm' }, { type: 'Access' }, { type: 'Perm', id: result?.results.id }]
        }),
        editAccessPermission: builder.mutation<StatusResponse<AccessPermission>, Partial<AccessPermissionRequest>>({
            query: (data: AccessPermissionRequest) => ({
                url: `be/api/v1/content/access/permissions/${data.id}/`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Perm' }, { type: 'Perm', id: result?.results.id }]
        }),
        deleteAccessPermission: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/content/access/permissions/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: () => [{ type: 'Perm' }]
        }),
        updateAccessPermissionStatus: builder.mutation<StatusResponse<AccessPermission>, { id: number, status: AccessPermissionStatus, updated?: string }>({
            query: (data) => ({
                url: `be/api/v1/content/access/permissions/${data.id}/status/`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Perm' }, { type: 'Perm', id: result?.results.id }]
        }),
        setAccessPermissionComment: builder.mutation<StatusResponse<AccessPermission>, { id: number, comment: string, updated?: string }>({
            query: (data) => ({
                url: `be/api/v1/content/access/permissions/${data.id}/comment/`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Perm' }, { type: 'Perm', id: result?.results.id }]
        }),
        getPermissionAvailUnits: builder.query<StatusPagesResponse<PermissionUnit>, number | void>({
            query: (account) => `be/api/v1/content/access/permissions/available/units/?offset=0&limit=-1${account ? '&account=' + account : ''}`,
            providesTags: (result, error, id) => [{ type: 'Perm' }],
        }),
        getAccessPermissionsAccounts: builder.query<StatusResponse<Omit<AccountShort, 'logo'>[]>, void>({
            query: () => 'be/api/v1/content/access/permissions/accounts/',
        }),
        getProducts: builder.query<StatusPagesResponse<Product>, OffsetLimit>({
            query: (params: OffsetLimit) => `be/api/v1/deals/product/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: (result, error, id) => [{ type: 'Content' }],
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
            // providesTags: () => [{ type: 'Content' }],
        }),
        getProductsNew: builder.query<StatusPagesResponse<Product>, OffsetLimit>({
            query: (params: OffsetLimit) => `be/api/v1/deals/product/new/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: (result, error, id) => [{ type: 'Content' }],
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
            // providesTags: () => [{ type: 'Content' }],
        }),
        getProductsOffers: builder.query<StatusPagesResponse<Product>, OffsetLimit>({
            query: (params: OffsetLimit) => `be/api/v1/deals/product/offers/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`,
            providesTags: (result, error, id) => [{ type: 'Content' }],
            // serializeQueryArgs: ({ queryArgs, endpointName }) => {
            //     const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
            //     return end;
            // },
            // // // Always merge incoming data to the cache entry
            // merge: (currentCache, newItems, otherArgs) => {
            //     if (otherArgs.arg.offset === 0) {
            //         currentCache.results.data = newItems.results.data;
            //     } else {
            //         currentCache.results.data.push(...newItems.results.data)
            //     }
            // },
            // // Refetch when the page arg changes
            // forceRefetch({ currentArg, previousArg }) {
            //     return currentArg !== previousArg
            // },
            // providesTags: () => [{ type: 'Content' }],
        }),
        getProduct: builder.query<StatusResponse<ProductFull>, number>({
            query: (id) => `be/api/v1/deals/product/${id}/`,
        }),
        createProduct: builder.mutation<StatusResponse<ProductFull>, Omit<ProductRequest, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/deals/product/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result) => [{ type: 'Content' }, { type: 'Content', id: result?.results.id }]
        }),
        updateProduct: builder.mutation<StatusResponse<ProductFull>, Partial<ProductRequest>>({
            query: (data: ProductRequest) => ({
                url: `be/api/v1/deals/product/${data.id}/`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result) => [{ type: 'Content' }, { type: 'Content', id: result?.results.id }]
        }),
        updateProductCms: builder.mutation<StatusResponse<ContentCms>, Partial<ContentCms>>({
            query: (data) => ({
                url: `/be/api/v1/deals/product/${data.id}/cms/`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result) => [{ type: 'Content' }, { type: 'Content', id: result?.results.id }]
        }),
        updateContract: builder.mutation<StatusResponse<Contract>, Partial<ContractRequest>>({
            query: (data: ContractRequest) => ({
                url: `be/api/v1/deals/contract/${data.id}/`,
                method: 'PUT',
                body: data,
            }),
            // invalidatesTags: (result, error, { id }) => [{ type: 'Perm' }, { type: 'Perm', id: result?.results.id }]
        }),
        deleteProduct: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/deals/product/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, id) => [{ type: 'Content' }, { type: 'Content', id: id }]
        }),
        getProductReferenceBook: builder.query<StatusResponse<ProductReferenceBooks>, void>({
            query: () => 'be/api/v1/deals/product/reference_books/',
            // providesTags: () => [{ type: 'Content' }],
        }),
        getProductIdReferenceBook: builder.query<StatusResponse<ProductReferenceBooks>, number>({
            query: (id) => `be/api/v1/deals/product/${id}/reference_books/`,
        }),
        getProductUserBook: builder.query<StatusResponse<ProductUserBooks>, void>({
            query: () => 'be/api/v1/deals/product/user_books/',
        }),
        getProductIdUserBook: builder.query<StatusResponse<ProductUserBooks>, number>({
            query: (id) => `be/api/v1/deals/product/${id}/user_books/`,
        }),
        getProductUsedBook: builder.query<StatusResponse<ProductUsedBooks>, void>({
            query: () => 'be/api/v1/deals/product/used_books/',
        }),
        createProductUserBooksItem: builder.mutation<StatusResponse<UserBooksResponse[]>, UserBooksRequest>({
            query: (data) => ({
                url: 'be/api/v1/deals/product/manage/userbooks/',
                method: 'POST',
                body: data
            })
        }),
        createProductIdUserBooksItem: builder.mutation<StatusResponse<UserBooksResponse[]>, { service_id: number, data: UserBooksRequest }>({
            query: ({ service_id, data }) => ({
                url: `be/api/v1/deals/product/${service_id}/manage/userbooks/`,
                method: 'POST',
                body: data
            })
        }),
        deleteProductUserBooksItem: builder.mutation<StatusResponse<null>, number>({
            query: (tag_id) => ({
                url: `be/api/v1/deals/product/manage/userbooks/${tag_id}/`,
                method: 'DELETE',
            })
        }),
        deleteProductIdUserBooksItem: builder.mutation<StatusResponse<null>, { service_id: number, item_id: number }>({
            query: ({ service_id, item_id: tag_id }) => ({
                url: `be/api/v1/deals/product/${service_id}/manage/userbooks/${tag_id}/`,
                method: 'DELETE',
            })
        }),
        getProductsAccounts: builder.query<StatusResponse<Omit<AccountShort, 'logo'>[]>, void>({
            query: (id) => 'be/api/v1/deals/product/accounts/',
        }),

        getContracts: builder.query<StatusPagesResponse<Contract>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/deals/contract/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
            providesTags: (result, error, id) => [{ type: 'Perm' }],
        }),
        getContract: builder.query<StatusResponse<Contract>, number>({
            query: (id) => `be/api/v1/deals/contract/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Perm' }],
        }),
        createContract: builder.mutation<StatusResponse<Contract>, Omit<ContractRequest, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/deals/contract/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'Perm' }]
        }),
        setContractComment: builder.mutation<StatusResponse<Contract>, { id: number, comment: string, updated?: string }>({
            query: (data: ContractRequest) => ({
                url: `be/api/v1/deals/contract/${data.id}/comment/`,
                method: 'POST',
                body: data,
            }),
            // invalidatesTags: (result, error) => [{ type: 'Perm' }]
        }),
        deleteContract: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/deals/contract/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'Perm' }]
        }),
        getContractAvailUnits: builder.query<StatusPagesResponse<ContractUnit>, void>({
            query: () => 'be/api/v1/deals/contract/available/units/?offset=0&limit=-1',
            providesTags: (result, error, id) => [{ type: 'Perm' }],
        }),
        getRegistry: builder.query<StatusPagesResponse<RegistryRecord>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/deals/registry/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
            providesTags: (result, error, id) => [{ type: 'Perm' }],
        }),
        getRegistryAccounts: builder.query<StatusResponse<Omit<AccountShort, 'logo'>[]>, void>({
            query: () => 'be/api/v1/deals/registry/accounts/',
        }),
        getPortfolio: builder.query<StatusPagesResponse<PortfolioRecord>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/deals/crm/portfolio/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
        }),
        getPortfolioDetail: builder.query<StatusPagesResponse<PortfolioRecordDetail>, OffsetLimit & { consumer_id: number, unit_id: number, provider_id: number }>({
            query: (params) => {
                return `be/api/v1/deals/crm/portfolio/group/${params.consumer_id}/${params.unit_id}/${params.provider_id}/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getPortfolioAccounts: builder.query<StatusResponse<Omit<AccountShort, 'logo'>[]>, void>({
            query: (id) => 'be/api/v1/deals/crm/portfolio/accounts/',
        }),
        getCrmConsumers: builder.query<StatusPagesResponse<CrmConsumerRecord>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/deals/crm/consumers/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getCrmConsumersDetail: builder.query<StatusResponse<CrmConsumerRecordDetail[]>, { provider_id: number, consumer_id: number }>({
            query: ({ provider_id, consumer_id }) => `be/api/v1/deals/crm/consumers/group/${provider_id}/${consumer_id}/`,
        }),
        getCrmConsumersAccounts: builder.query<StatusResponse<AccountListElement[]>, void>({
            query: (id) => 'be/api/v1/deals/crm/consumers/accounts/',
        }),
        getCrmProducts: builder.query<StatusPagesResponse<CrmProductRecord>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/deals/crm/products/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
        }),
        getCrmProductsDetail: builder.query<StatusResponse<CrmProductRecordDetail[]>, { provider_id: number, unit_id: number }>({
            query: ({ provider_id, unit_id }) => `be/api/v1/deals/crm/products/group/${provider_id}/${unit_id}/`,
        }),
        getCrmProductsAccounts: builder.query<StatusResponse<Omit<AccountShort, 'logo'>[]>, void>({
            query: (id) => 'be/api/v1/deals/crm/products/accounts/',
        }),
        getCrmSourceConsumers: builder.query<StatusPagesResponse<CrmSourceConsumer>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/deals/crm/source/consumers/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
            providesTags: (result, error, id) => [{ type: 'Crm' }],
        }),
        getCrmSourceConsumersGroup: builder.query<StatusPagesResponse<CrmSourceConsumerGroup>, number>({
            query: (consumer_id) => `be/api/v1/deals/crm/source/consumers/group/${consumer_id}/?offset=0&limit=-1`,
            providesTags: (result, error, id) => [{ type: 'Crm' }],
        }),
        getCrmSourceConsumersAccounts: builder.query<StatusResponse<Omit<AccountShort, 'logo'>[]>, void>({
            query: (id) => 'be/api/v1/deals/crm/source/consumers/accounts/',
        }),

        getTVSignals: builder.query<StatusPagesResponse<TVSignal>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/tv/signal/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
            providesTags: () => [{ type: 'Crm' }],
        }),
        getTVSignal: builder.query<StatusResponse<TVSignal>, number>({
            query: (id) => `be/api/v1/tv/signal/${id}/`,
        }),
        createTVSignal: builder.mutation<StatusResponse<TVSignal>, Omit<TVSignalRequest, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/tv/signal/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'Crm' }]
        }),
        updateTVSignal: builder.mutation<StatusResponse<TVSignal>, Partial<TVSignalRequest>>({
            query: (data) => ({
                url: `be/api/v1/tv/signal/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'Crm' }]
        }),
        deleteTVSignal: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/tv/signal/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'Crm' }]
        }),

        getTVSignalStates: builder.query<StatusPagesResponse<TVSignalState>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/tv/signal/state/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
            // providesTags: () => [{ type: 'File' }],
        }),
        getTVSignalState: builder.query<StatusResponse<TVSignalState>, number>({
            query: (id) => `be/api/v1/tv/signal/state/${id}/`,
        }),
        createTVSignalState: builder.mutation<StatusResponse<TVSignalState>, Omit<TVSignalStateRequest, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/tv/signal/state/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'Crm' }]
        }),
        updateTVSignalState: builder.mutation<StatusResponse<TVSignalState>, Partial<TVSignalStateRequest>>({
            query: (data) => ({
                url: `be/api/v1/tv/signal/state/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'Crm' }]
        }),
        deleteTVSignalState: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/tv/signal/state/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'Crm' }]
        }),

        getFiles: builder.query<StatusPagesResponse<FileDescription>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/files/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            serializeQueryArgs: ({ queryArgs, endpointName }) => {
                const end = endpointName + JSON.stringify(Object.entries(queryArgs).filter(([k, v]) => !['offset', 'limit'].includes(k) && !!v));
                return end;
            },
            // // Always merge incoming data to the cache entry
            merge: (currentCache, newItems, otherArgs) => {
                if (otherArgs.arg.offset === 0) {
                    currentCache.results.data = newItems.results.data;
                } else {
                    currentCache.results.data.push(...newItems.results.data)
                }
            },
            // Refetch when the page arg changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg
            },
            // providesTags: () => [{ type: 'File' }],
        }),
        getFile: builder.query<StatusResponse<FileDescription>, number>({
            query: (id) => `be/api/v1/files/${id}/`,
        }),
        attachFile: builder.mutation<void, { id: number, file_type: FileType, file_id: number }>({
            query: ({ id, file_id, file_type }) => ({
                url: `be/api/v1/content/${id}/files/attach/${file_type}/${file_id}/`,
                method: 'POST',
            }),
        }),
        deleteFile: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/files/${id}/`,
                method: 'DELETE',
            }),
        }),
        getUploadsV1: builder.query<StatusResponse<{ data: Upload[] }>, void>({
            query: () => 'be/api/v1/uploads/',
            // providesTags: (result, error, id) => [{ type: 'Content' }, { type: 'Upload' }],
        }),
        getUploadV1: builder.query<StatusResponse<{ data: UploadFull }>, string>({
            query: (id) => `be/api/v1/uploads/${id}/`,
            // providesTags: (result, error, id) => [{ type: 'Content' }, { type: 'Upload' }],
        }),
        createUploadV1: builder.mutation<StatusResponse<UploadShort>, UploadRequest>({
            query(data) {
                const formData = new FormData();
                formData.append('file', data.file);
                Object.entries(data).forEach(([k, v]) => formData.append(k, v))
                return {
                    url: 'be/api/v1/uploads/',
                    method: 'POST',
                    body: formData,
                    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                        data.onProgress && data.onProgress(progressEvent)
                    },
                };
            },
            // invalidatesTags: (result, error, { id }) => [{ type: 'Perm' }, { type: 'Access' }, { type: 'Perm', id: result?.results.id }]
        }),
        createRemoteUpload: builder.mutation<StatusResponse<UploadShort>, { cat: FSObjectCategory, source: string }>({
            query(data) {
                return {
                    url: 'be/api/uploads/remote/',
                    method: 'POST',
                    body: { cat: null, source: data.source },
                };
            },
            // invalidatesTags: (result, error, { id }) => [{ type: 'Perm' }, { type: 'Access' }, { type: 'Perm', id: result?.results.id }]
        }),
        deleteUploadV1: builder.mutation<void, string>({
            query: (id) => ({
                url: `be/api/v1/uploads/${id}/`,
                method: 'DELETE',
            }),
        }),
        uploadMultipart: builder.mutation<StatusResponse<UploadResponse>, UploadMultipartRequest>({
            query: (args) => {
                const params = new URLSearchParams();
                params.set('uuid', args.uuid);
                params.set('chunk', args.chunkIndex.toString());
                params.set('total_chunks', args.chunkCount.toString());
                params.set('fullsize', args.fullsize.toString());
                params.set('orig_name', args.orig_name);
                params.set('crc', args.crc);
                params.set('cat', args.cat.toString());
                params.set('chunk_size', args.chunk_size.toString());
                args.account_id && params.set('account_id', args.account_id.toString());

                return {
                    url: `be/api/v1/uploads/multipart/?${params.toString()}`,
                    method: 'POST',
                    body: args.data,
                    headers: { 'Content-Type': 'application/octet-stream' },
                    signal: args.signal,
                }
            },
        }),
        getTVDBChannels: builder.query<StatusResponse<TVDBChannel[]>, { search?: string } | void>({
            query: (d) => `be/api/v1/tvdb/tv/channels/?search=${d?.search || ''}`,
            providesTags: (result, error, id) => [{ type: 'Tvdb' }],
        }),
        getTVDBChannel: builder.query<StatusResponse<TVDBChannel>, number>({
            query: (id) => `be/api/v1/tvdb/tv/channels/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Tvdb' }],
        }),
        createTVDBChannel: builder.mutation<StatusResponse<TVDBChannel>, Omit<TVDBChannelRequest, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/tvdb/tv/channels/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [{ type: 'Tvdb' }]
        }),
        updateTVDBChannel: builder.mutation<StatusResponse<TVDBChannel>, Partial<TVDBChannelRequest>>({
            query: (data: ContractRequest) => ({
                url: `be/api/v1/tvdb/tv/channels/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: [{ type: 'Tvdb' }]
        }),
        deleteTVDBChannel: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/tvdb/tv/channels/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'Tvdb' }]
        }),
        execPipeline: builder.mutation<StatusResponse<{
            executed: boolean, // - если вызов был выполнен true, если не false
            method: string,    // - параметры вызова
            url: string,
            label: string,
            response: { // - ответ
                code: number, // - http code ответа
                json: boolean,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data: StatusResponse<any>
            }
        }[]>, {
            pipeline: {
                method: string, // - метод
                url: string, // - url api, можно параметры указывать через ?
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data: any //
                label?: string // - необязательное поле (потом может пригодится при добавлении переменных)
            }[]
        }>({
            query: (data) => ({
                url: 'be/api/v1/pipeline/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error, id) => [{ type: 'Content' }]
        }),
        // Dictionaries manage
        getDictionaries: builder.query<StatusResponse<MBDictionaryDescription[]>, void>({
            query: () => {
                return 'be/api/v1/dictionaries/'
            },
            // providesTags: () => [{ type: 'Dict' }],
        }),
        getDictionary: builder.query<StatusPagesResponse<MBDictionary>, string>({
            query: (id) => `be/api/v1/dictionaries/${id}/?offset=0&limit=-1`,
            providesTags: () => [{ type: 'Dict' }],
        }),
        getDictionaryRecord: builder.query<StatusResponse<MBDictionary>, [string, number]>({
            query: ([did, id]) => `be/api/v1/dictionaries/${did}/${id}/`,
        }),
        createDictionaryRecord: builder.mutation<StatusResponse<MBDictionary>, Omit<MBDictionaryRequest, 'id'>>({
            query: (data) => ({
                url: `be/api/v1/dictionaries/${data.did}/`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'Dict' }]
        }),
        updateDictionaryRecord: builder.mutation<StatusResponse<MBDictionary>, Partial<MBDictionaryRequest>>({
            query: (data) => ({
                url: `be/api/v1/dictionaries/${data.did}/${data.id}/`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'Dict' }]
        }),
        deleteDictionaryRecord: builder.mutation<void, { did: string, id: number, force?: boolean }>({
            query: (data) => ({
                url: `be/api/v1/dictionaries/${data.did}/${data.id}/${data.force ? '?force=yes' : ''}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'Dict' }]
        }),
        // TVDB KK
        getTVDBKKMonitoring: builder.query<StatusPagesResponse<TVDBKKStatus>, void>({
            query: () => 'be/api/v1/tvdb/kk/monitoring/?offset=0&limit=-1',
            providesTags: [{ type: 'TvdbKK' }],
        }),
        getTVDBKKConsumers: builder.query<StatusPagesResponse<TVDBKKConsumer>, void>({
            query: () => 'be/api/v1/tvdb/kk/consumers/?offset=0&limit=-1',
            providesTags: [{ type: 'TvdbKK' }],
        }),
        getTVDBKKConsumersDetail: builder.query<StatusResponse<TVDBKKConsumersDetail[]>, number>({
            query: (consumer_id) => `be/api/v1/tvdb/kk/consumers/${consumer_id}/`,
        }),
        getTVDBKKSignals: builder.query<StatusPagesResponse<TVDBKKSignal>, void>({
            query: () => 'be/api/v1/tvdb/kk/signals/?offset=0&limit=-1',
            providesTags: [{ type: 'TvdbKK' }],
        }),
        getTVDBKKAccess: builder.query<StatusPagesResponse<TVDBKKAccess>, void>({
            query: () => 'be/api/v1/tvdb/kk/access/?offset=0&limit=-1',
            providesTags: [{ type: 'TvdbKK' }],
        }),
        getBTech: builder.query<BTechData, { id: number, sigId: number, smode: string, mode: BTechGraphType, start?: number, finish?: number, quality: string }>({
            query: ({ id, sigId, smode, start, finish, mode, quality }) =>
                `be/api/v1/tvdb/kk/btech/${sigId}/?smode=${smode}&tuning_id=${id}&mode=${mode}&start=${start}&finish=${finish}&quality=${quality}`,
        }),
        // CMS
        // работа с файлами
        getCMSFiles: builder.query<StatusPagesResponse<CMSFile>, void>({
            query: () => 'be/api/v1/cms/admin/files/',
            // providesTags: (result, error, id) => [{ type: 'Content' }, { type: 'Upload' }],
        }),
        getCMSFile: builder.query<StatusResponse<CMSFile>, number>({
            query: (id) => `be/api/v1/cms/admin/files/${id}/`,
            // providesTags: (result, error, id) => [{ type: 'Content' }, { type: 'Upload' }],
        }),
        createCMSFile: builder.mutation<StatusResponse<CMSFile>, CMSFileUpload>({
            query(data) {
                const formData = new FormData();
                formData.append('file', data.file);
                Object.entries(data).forEach(([k, v]) => formData.append(k, v))
                return {
                    url: 'be/api/v1/cms/admin/files/',
                    method: 'POST',
                    body: formData,
                    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                        data.onProgress && data.onProgress(progressEvent)
                    },
                };
            },
        }),
        deleteCMSFile: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/cms/admin/files/${id}/`,
                method: 'DELETE',
            }),
        }),
        // Новости разделы
        getNewsTopics: builder.query<StatusResponse<NewsTopic[]>, void>({
            query: () => 'be/api/v1/cms/news/topics/',
            providesTags: () => [{ type: 'CMSNews' }],
        }),
        getAdminNewsTopics: builder.query<StatusResponse<NewsAdminTopic[]>, void>({
            query: () => 'be/api/v1/cms/admin/news/topics/',
            providesTags: () => [{ type: 'CMSNews' }],
        }),
        getNewsTopic: builder.query<StatusResponse<NewsAdminTopic>, number>({
            query: (id) => `be/api/v1/cms/admin/news/topics/${id}/`,
        }),
        createNewsTopic: builder.mutation<StatusResponse<NewsAdminTopic>, Omit<NewsAdminTopic, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/cms/admin/news/topics/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSNews' }]
        }),
        updateNewsTopic: builder.mutation<StatusResponse<NewsAdminTopic>, Partial<NewsAdminTopic>>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/news/topics/${data.id}/`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSNews' }]
        }),
        deleteNewsTopic: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/cms/admin/news/topics/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSNews' }]
        }),
        // Новости
        getAllNews: builder.query<StatusPagesResponse<News>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/news/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/news/',
            providesTags: () => [{ type: 'CMSNews' }],
        }),
        getNews: builder.query<StatusResponse<News>, number>({
            query: (id) => `be/api/v1/cms/news/${id}/`,
        }),
        getAllAdminNews: builder.query<StatusPagesResponse<NewsAdmin>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/admin/news/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/admin/news/',
            providesTags: () => [{ type: 'CMSNews' }],
        }),
        getAdminNews: builder.query<StatusResponse<NewsAdmin>, number>({
            query: (id) => `be/api/v1/cms/admin/news/${id}/`,
        }),
        createNews: builder.mutation<StatusResponse<NewsAdmin>, Omit<NewsAdmin, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/cms/admin/news/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSNews' }]
        }),
        updateNews: builder.mutation<StatusResponse<NewsAdmin>, Partial<NewsAdmin>>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/news/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSNews' }]
        }),
        deleteNews: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/cms/admin/news/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSNews' }]
        }),
        // Акции
        getAllPromo: builder.query<StatusPagesResponse<Promo>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/promo/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/promo/',
            providesTags: () => [{ type: 'CMSPromo' }],
        }),
        getPromo: builder.query<StatusResponse<Promo>, number>({
            query: (id) => `be/api/v1/cms/promo/${id}/`,
        }),
        getAllAdminPromo: builder.query<StatusPagesResponse<PromoAdmin>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/admin/promo/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/admin/promo/',
            providesTags: () => [{ type: 'CMSPromo' }],
        }),
        getAdminPromo: builder.query<StatusResponse<PromoAdmin>, number>({
            query: (id) => `be/api/v1/cms/admin/promo/${id}/`,
        }),
        createPromo: builder.mutation<StatusResponse<PromoAdmin>, Omit<PromoAdmin, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/cms/admin/promo/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSPromo' }]
        }),
        updatePromo: builder.mutation<StatusResponse<PromoAdmin>, Partial<PromoAdmin>>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/promo/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSPromo' }]
        }),
        deletePromo: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/cms/admin/promo/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSPromo' }]
        }),
        // Голосования
        getAllPolls: builder.query<StatusPagesResponse<Polls>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/polls/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/polls/',
            providesTags: () => [{ type: 'CMSPolls' }],
        }),
        getPoll: builder.query<StatusResponse<Polls>, number>({
            query: (id) => `be/api/v1/cms/polls/${id}/`,
            providesTags: () => [{ type: 'CMSPolls' }],
        }),
        postVote: builder.mutation<StatusResponse<Polls>, { option: number, id: number }>({
            query: (data) => ({
                url: `/be/api/v1/cms/polls/${data.id}/vote/`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSPolls' }]
        }),
        getAllAdminPolls: builder.query<StatusPagesResponse<PollsAdmin>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/admin/polls/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/admin/polls/',
            providesTags: () => [{ type: 'CMSPolls' }],
        }),
        getPolls: builder.query<StatusResponse<PollsAdmin>, number>({
            query: (id) => `be/api/v1/cms/admin/polls/${id}/`,
            providesTags: () => [{ type: 'CMSPolls' }],
        }),
        createPolls: builder.mutation<StatusResponse<PollsAdmin>, Omit<PollsAdmin, 'id' | 'options'>>({
            query: (data) => ({
                url: 'be/api/v1/cms/admin/polls/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSPolls' }]
        }),
        getPollsOptions: builder.query<StatusResponse<PollsOptionFull[]>, number>({
            query: (id) => `be/api/v1/cms/admin/polls/${id}/options/`,
            providesTags: () => [{ type: 'CMSPolls' }],
        }),
        getPollsOption: builder.query<StatusResponse<PollsOptionFull>, [number, number]>({
            query: ([id, oid]) => `be/api/v1/cms/admin/polls/${id}/options/${oid}/`,
            providesTags: () => [{ type: 'CMSPolls' }],
        }),
        createPollsOptions: builder.mutation<StatusResponse<PollsOptionFull>, PollsOption & { id: number }>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/polls/${data.id}/options/`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSPolls' }]
        }),
        updatePollsOptions: builder.mutation<StatusResponse<PollsOptionFull>, Partial<PollsOption & { pid: number, id: number }>>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/polls/${data.pid}/options/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSPolls' }]
        }),
        deletePollsOption: builder.mutation<void, [number, number]>({
            query: ([id, oid]) => ({
                url: `be/api/v1/cms/admin/polls/${id}/options/${oid}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSPolls' }]
        }),
        updatePolls: builder.mutation<StatusResponse<PollsAdmin>, Partial<PollsAdmin>>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/polls/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSPolls' }]
        }),
        deletePolls: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/cms/admin/polls/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSPolls' }]
        }),
        // Объявления
        getAllAdverts: builder.query<StatusPagesResponse<Adverts>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/adverts/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/adverts/',
            providesTags: () => [{ type: 'CMSAdverts' }],
        }),
        getAdvert: builder.query<StatusResponse<Adverts>, number>({
            query: (id) => `be/api/v1/cms/adverts/${id}/`,
            providesTags: () => [{ type: 'CMSAdverts' }],
        }),
        getAllAdminAdverts: builder.query<StatusPagesResponse<AdvertsAdmin>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/admin/adverts/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/admin/adverts/',
            providesTags: () => [{ type: 'CMSAdverts' }],
        }),
        getAdverts: builder.query<StatusResponse<AdvertsAdmin>, number>({
            query: (id) => `be/api/v1/cms/admin/adverts/${id}/`,
            providesTags: () => [{ type: 'CMSAdverts' }],
        }),
        createAdverts: builder.mutation<StatusResponse<AdvertsAdmin>, Omit<AdvertsAdmin, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/cms/admin/adverts/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSAdverts' }]
        }),
        updateAdverts: builder.mutation<StatusResponse<AdvertsAdmin>, Partial<AdvertsAdmin>>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/adverts/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSAdverts' }]
        }),
        deleteAdverts: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/cms/admin/adverts/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSAdverts' }]
        }),
        // Баннеры
        getAllBanners: builder.query<StatusPagesResponse<Banner>, void>({
            query: () => 'be/api/v1/cms/banners/',
            providesTags: () => [{ type: 'Dict' }],
        }),
        getAllAdminBanners: builder.query<StatusPagesResponse<BannersAdmin>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/admin/banners/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/admin/banners/',
            providesTags: () => [{ type: 'CMSBanners' }],
        }),
        getBanners: builder.query<StatusResponse<BannersAdmin>, number>({
            query: (id) => `be/api/v1/cms/admin/banners/${id}/`,
            providesTags: () => [{ type: 'CMSBanners' }],
        }),
        createBanners: builder.mutation<StatusResponse<BannersAdmin>, Omit<BannersAdmin, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/cms/admin/banners/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSBanners' }]
        }),
        updateBanners: builder.mutation<StatusResponse<BannersAdmin>, Partial<BannersAdmin>>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/banners/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSBanners' }]
        }),
        deleteBanners: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/cms/admin/banners/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSBanners' }]
        }),
        // Уведомления
        getAllNotifications: builder.query<StatusResponse<NotificationUnit[]>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/notifications/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/notifications/',
            providesTags: () => [{ type: 'CMSNotify' }],
        }),
        getNotification: builder.query<StatusResponse<NotificationUnit>, number>({
            query: (id) => `be/api/v1/cms/notifications/${id}/`,
            providesTags: () => [{ type: 'CMSNotify' }],
        }),
        getAllAdminNotifications: builder.query<StatusPagesResponse<AdminNotification>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/admin/notifications/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/admin/notifications/',
            providesTags: () => [{ type: 'CMSNotify' }],
        }),
        getAdminNotification: builder.query<StatusResponse<AdminNotification>, number>({
            query: (id) => `be/api/v1/cms/admin/notifications/${id}/`,
            providesTags: () => [{ type: 'CMSNotify' }],
        }),
        createNotifications: builder.mutation<StatusResponse<AdminNotification>, Omit<AdminNotification, 'id' | 'active'>>({
            query: (data) => ({
                url: 'be/api/v1/cms/admin/notifications/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSNotify' }]
        }),
        updateNotifications: builder.mutation<StatusResponse<AdminNotification>, Partial<AdminNotification>>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/notifications/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSNotify' }]
        }),
        deleteNotifications: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/cms/admin/notifications/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSNotify' }]
        }),
        // 12. Рекламные баннеры (позиционные)
        getAdvBanners: builder.query<StatusPagesResponse<AdvBanner>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/admin/advertising/banners/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/admin/advertising/banners/',
            providesTags: () => [{ type: 'CMSAdverts' }],
        }),
        getAdvBanner: builder.query<StatusResponse<AdvBanner>, number>({
            query: (id) => `be/api/v1/cms/admin/advertising/banners/${id}/`,
            providesTags: () => [{ type: 'CMSAdverts' }],
        }),
        createAdvBanner: builder.mutation<StatusResponse<AdvBanner>, Omit<AdvAdminBanner, 'id'>>({
            query: (data) => ({
                url: 'be/api/v1/cms/admin/advertising/banners/',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSAdverts' }]
        }),
        updateAdvBanner: builder.mutation<StatusResponse<AdvBanner>, Partial<AdvAdminBanner>>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/advertising/banners/${data.id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSAdverts' }]
        }),
        deleteAdvBanner: builder.mutation<void, number>({
            query: (id) => ({
                url: `be/api/v1/cms/admin/advertising/banners/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSAdverts' }]
        }),
        // Главная страница
        getIndexBlocks: builder.query<StatusPagesResponse<CmsIndexBlock>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/admin/blocks/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/admin/blocks/',
            providesTags: () => [{ type: 'CMSIndex' }],
        }),
        getIndexBlock: builder.query<StatusResponse<CmsIndexBlock>, number>({
            query: (id) => `be/api/v1/cms/admin/blocks/${id}/`,
            providesTags: () => [{ type: 'CMSIndex' }],
        }),
        getIndexBlockAvailable: builder.query<StatusPagesResponse<CmsContentType>, number>({
            query: (id) => `be/api/v1/cms/admin/blocks/${id}/available/pages/`,
            // providesTags: () => [{ type: 'CMSIndex' }],
        }),
        updateIndexBlock: builder.mutation<StatusResponse<CmsContentType>, { items: number[], id: number }>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/blocks/${data.id}/`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSIndex' }]
        }),
        // Public
        getPublicBlocks: builder.query<StatusResponse<PublicBloks[]>, void>({
            query: () => 'be/api/v1/cms/public/blocks/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicNewsTopics: builder.query<StatusResponse<NewsTopic[]>, void>({
            query: () => 'be/api/v1/cms/public/news/topics/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicNews: builder.query<StatusPagesResponse<News>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/public/news/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                :
                'be/api/v1/cms/public/news/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicNewsOne: builder.query<StatusResponse<News>, number>({
            query: (id) => `be/api/v1/cms/public/news/${id}/`,
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicPolls: builder.query<StatusPagesResponse<Polls>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/public/polls/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/public/polls/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicPoll: builder.query<StatusResponse<Polls>, number>({
            query: (id) => `be/api/v1/cms/public/polls/${id}/`,
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicPromos: builder.query<StatusPagesResponse<Promo>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/public/promo/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/public/promo/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicPromo: builder.query<StatusResponse<Promo>, number>({
            query: (id) => `be/api/v1/cms/public/promo/${id}/`,
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicAdverts: builder.query<StatusPagesResponse<Adverts>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/public/adverts/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/public/adverts/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicAdvert: builder.query<StatusResponse<Adverts>, number>({
            query: (id) => `be/api/v1/cms/public/adverts/${id}/`,
            // providesTags: () => [{ type: 'Public' }],
        }),
        // Реклама
        getAdvBlocks: builder.query<StatusPagesResponse<CmsIndexBlock>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/admin/advertising/blocks/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/admin/advertising/blocks/',
            providesTags: () => [{ type: 'CMSIndex' }],
        }),
        getAdvBlock: builder.query<StatusResponse<CmsIndexBlock>, number>({
            query: (id) => `be/api/v1/cms/admin/advertising/blocks/${id}/`,
            providesTags: () => [{ type: 'CMSIndex' }],
        }),
        getAdvBlockAvailable: builder.query<StatusPagesResponse<CmsContentType>, number>({
            query: (id) => `be/api/v1/cms/admin/advertising/blocks/${id}/available/pages/`,
            providesTags: () => [{ type: 'CMSIndex' }],
        }),
        updateAdvBlock: builder.mutation<StatusResponse<CmsContentType>, { items: number[], id: number }>({
            query: (data) => ({
                url: `be/api/v1/cms/admin/advertising/blocks/${data.id}/`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (result, error) => [{ type: 'CMSIndex' }]
        }),
        getAdvPublicBlocks: builder.query<StatusResponse<CmsIndexBlock>, void>({
            query: () => 'be/api/v1/cms/admin/advertising/blocks/',
        }),
        getAdvPublicBlock: builder.query<StatusResponse<CmsIndexBlock>, 'advertising-banners-1' | 'advertising-banners-2'>({
            query: (id) => `be/api/v1/cms/admin/advertising/blocks/${id}/`,
        }),
        // Витрина
        getPublicRecommends: builder.query<StatusPagesResponse<MediaProduct>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/public/recommends/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/public/recommends/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicRecommend: builder.query<StatusResponse<MediaProduct>, number>({
            query: (id) => `be/api/v1/cms/public/recommends/${id}/`,
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicMedias: builder.query<StatusPagesResponse<MediaProduct<MediaUnit, 'media'>>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/public/media/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/public/media/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicMedia: builder.query<StatusResponse<MediaProduct<MediaUnit, 'media'>>, number>({
            query: (id) => `be/api/v1/cms/public/media/${id}/`,
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicTVs: builder.query<StatusPagesResponse<MediaProduct<TVUnit, 'tv'>>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/public/tv/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/public/tv/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicTV: builder.query<StatusResponse<MediaProduct<TVUnit, 'tv'>>, number>({
            query: (id) => `be/api/v1/cms/public/tv/${id}/`,
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicProducts: builder.query<StatusPagesResponse<MediaProduct<ProductUnit, 'product'>>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/public/products/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/public/products/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicProduct: builder.query<StatusResponse<MediaProduct<ProductUnit, 'product'>>, number>({
            query: (id) => `be/api/v1/cms/public/products/${id}/`,
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicNotifications: builder.query<StatusResponse<NotificationUnit[]>, void | OffsetLimit>({
            query: (params) => params ?
                `be/api/v1/cms/public/notifications/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
                : 'be/api/v1/cms/public/notifications/',
            // providesTags: () => [{ type: 'Public' }],
        }),
        getPublicNotification: builder.query<StatusResponse<NotificationUnit>, number>({
            query: (id) => `be/api/v1/cms/public/notifications/${id}/`,
            // providesTags: () => [{ type: 'Public' }],
        }),
        // Публичный api баннеров
        getPublicAdvBanners: builder.query<StatusResponse<PublicBanner[]>, void>({
            query: () => 'be/api/v1/cms/public/advertising/blocks/',
        }),
        getPublicAdvBanner: builder.query<StatusResponse<PublicBanner>, 'advertising-banners-1' | 'advertising-banners-2'>({
            query: (label) => `be/api/v1/cms/public/advertising/blocks/${label}/`,
        }),
        // генерация статики
        getOperations: builder.query<StatusResponse<Operation[]>, void>({
            query: () => 'be/api/v1/system/operations/',
        }),
        getOperationStatus: builder.query<StatusResponse<OperationFull>, string>({
            query: (id) => `be/api/v1/system/operations/${id}/`,
        }),
        generateSiteOperation: builder.mutation<StatusResponse<Operation>, void | 'all' | 'only-news' | 'except-news'>({
            query: (data) => ({
                url: 'be/api/v1/system/operations/',
                method: 'POST',
                body: {
                    command: 'generate-static',
                    parameters: {
                        'type': data || 'only-news'
                    }
                },
            }),
        }),
        // публичный доступ по ссылке
        getPublicContents: builder.query<StatusPagesResponse<PublicContent>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/content/public/links/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            providesTags: () => [{ type: 'PublicContent' }],
        }),
        getPublicContent: builder.query<StatusResponse<PublicContent>, number>({
            query: (id: number) => `be/api/v1/content/public/links/${id}/`,
            providesTags: (result, error, id) => [{ type: 'PublicContent', id }],
        }),
        createPublicContent: builder.mutation<StatusResponse<PublicContent>, Pick<PublicContent, 'unit' | 'expiry'>>({
            query: (data: PublicContent) => ({
                url: 'be/api/v1/content/public/links/',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'PublicContent' }]
        }),
        editPublicContent: builder.mutation<StatusResponse<PublicContent>, Pick<PublicContent, 'id' | 'unit' | 'expiry'>>({
            query: (data: PublicContent) => ({
                url: `be/api/v1/content/public/links/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'PublicContent' }, { type: 'PublicContent', id: result?.results.id }]
        }),
        deletePublicContent: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `be/api/v1/content/public/links/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'PublicContent' }]
        }),
        getPublicContentProducts: builder.query<StatusPagesResponse<PublicProduct>, OffsetLimit>({
            query: (params: OffsetLimit) => {
                return `be/api/v1/deals/product/public/links/?${Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&')}`
            },
            providesTags: () => [{ type: 'PublicContent' }],
        }),
        getPublicContentProduct: builder.query<StatusResponse<PublicProduct>, number>({
            query: (id: number) => `be/api/v1/deals/product/public/links/${id}/`,
            providesTags: (result, error, id) => [{ type: 'PublicContent', id }],
        }),
        createPublicProduct: builder.mutation<StatusResponse<PublicProduct>, Pick<PublicProduct, 'product' | 'expiry'>>({
            query: (data: PublicProduct) => ({
                url: 'be/api/v1/deals/product/public/links/',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'PublicContent' }]
        }),
        editPublicProduct: builder.mutation<StatusResponse<PublicProduct>, Pick<PublicProduct, 'id' | 'product' | 'expiry'>>({
            query: (data: PublicProduct) => ({
                url: `be/api/v1/deals/product/public/links/${data.id}/`,
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'PublicContent' }, { type: 'PublicContent', id: result?.results.id }]
        }),
        deletePublicProduct: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `be/api/v1/deals/product/public/links/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'PublicContent' }]
        }),
        // with tokens
        getPublicContentUnit: builder.query<StatusResponse<ContentTVUnitFull | ContentUnitFull>, { token: string }>({
            query: ({ token }) => ({
                url: 'be/api/v1/content/link/',
                method: 'GET',
                token: token,
            })
        }),
        getPublicProductContent: builder.query<StatusResponse<ProductFull>, { token: string }>({
            query: ({ token }) => ({
                url: 'be/api/v1/deals/product/link/',
                method: 'GET',
                token: token,
            })
        }),
    }),
})

export const {
    useAccessRequestProductMutation,
    useAccessRequestsSetStatusMutation,
    useAccessRequestTVMutation,
    useAccessRequestUnitMutation,
    useAttachFileMutation,
    useCreateAccessPermissionMutation,
    useCreateAdvertsMutation,
    useCreateBannersMutation,
    useCreateCMSFileMutation,
    useCreateContentCoverMutation,
    useCreateContentTrailerMutation,
    useCreateContentUnitMutation,
    useCreateContentUserBooksItemMutation,
    useCreateContractMutation,
    useCreateDictionaryRecordMutation,
    useCreateDraftTVUnitMutation,
    useCreateDraftUnitMutation,
    useCreateNewsMutation,
    useCreateNewsTopicMutation,
    useCreatePollsMutation,
    useCreatePollsOptionsMutation,
    useCreateProductIdUserBooksItemMutation,
    useCreateProductMutation,
    useCreateProductUserBooksItemMutation,
    useCreatePromoMutation,
    useCreateTVDBChannelMutation,
    useCreateTVSignalMutation,
    useCreateTVSignalStateMutation,
    useCreateTVUnitMutation,
    useCreateUploadV1Mutation,
    useCreateUserBooksItemMutation,
    useDeleteAccessPermissionMutation,
    useDeleteAdvertsMutation,
    useDeleteBannersMutation,
    useDeleteCMSFileMutation,
    useDeleteContentCoverMutation,
    useDeleteContentMediaMutation,
    useDeleteContentTrailerMutation,
    useDeleteContentUnitMutation,
    useDeleteContentUserBooksItemMutation,
    useDeleteContractMutation,
    useDeleteDictionaryRecordMutation,
    useDeleteFileMutation,
    useDeleteNewsMutation,
    useDeleteNewsTopicMutation,
    useDeletePollsMutation,
    useDeletePollsOptionMutation,
    useDeleteProductIdUserBooksItemMutation,
    useDeleteProductMutation,
    useDeleteProductUserBooksItemMutation,
    useDeletePromoMutation,
    useDeleteTVDBChannelMutation,
    useDeleteTVSignalMutation,
    useDeleteTVSignalStateMutation,
    useDeleteTVUnitMutation,
    useDeleteUnitUploadMutation,
    useDeleteUploadV1Mutation,
    useDeleteUserBooksItemMutation,
    useEditAccessPermissionMutation,
    useEditContentUnitMutation,
    useEditTVUnitMutation,
    useExecPipelineMutation,
    useGetAccessPermissionDocxQuery,
    useGetAccessPermissionPdfQuery,
    useGetAccessPermissionQuery,
    useGetAccessPermissionsAccountsQuery,
    useGetAccessPermissionsQuery,
    useGetAccessRequestAllowedRecipientsQuery,
    useGetAccessRequestHistoryQuery,
    useGetAccessRequestMessageQuery,
    useGetAccessRequestMessageRecipientsQuery,
    useGetAccessRequestQuery,
    useGetAccessRequestsAccountsQuery,
    useGetAccessRequestsQuery,
    useGetAccessRequestsSummaryQuery,
    useGetAdminNewsQuery,
    useGetAdminNewsTopicsQuery,
    useGetAdminPromoQuery,
    useGetAdvertQuery,
    useGetAdvertsQuery,
    useGetAllAdminAdvertsQuery,
    useGetAllAdminBannersQuery,
    useGetAllAdminNewsQuery,
    useGetAllAdminPollsQuery,
    useGetAllAdminPromoQuery,
    useGetAllAdvertsQuery,
    useGetAllBannersQuery,
    useGetAllNewsQuery,
    useGetAllPollsQuery,
    useGetAllPromoQuery,
    useGetBannersQuery,
    useGetBTechQuery,
    useGetCheckedContentQuery,
    useGetCheckedTVQuery,
    useGetCMSFileQuery,
    useGetCMSFilesQuery,
    useGetContentCheckmarksFieldsQuery,
    useGetContentCheckmarksQuery,
    useGetContentComentsFieldsQuery,
    useGetContentComentsQuery,
    useGetContentDownloadInfoQuery,
    useGetContentHistoryQuery,
    useGetContentListQuery,
    useGetContentOffersQuery,
    useGetContentTVListCountQuery,
    useGetContentUnitCollectionsQuery,
    useGetContentUnitListCountQuery,
    useGetContentUnitListQuery,
    useGetContentUnitMediaQuery,
    useGetContentUnitQuery,
    useGetContentUserBookQuery,
    useGetContractAvailUnitsQuery,
    useGetContractQuery,
    useGetContractsQuery,
    useGetCoverMutation,
    useGetCrmConsumersAccountsQuery,
    useGetCrmConsumersDetailQuery,
    useGetCrmConsumersQuery,
    useGetCrmProductsAccountsQuery,
    useGetCrmProductsDetailQuery,
    useGetCrmProductsQuery,
    useGetCrmSourceConsumersAccountsQuery,
    useGetCrmSourceConsumersGroupQuery,
    useGetCrmSourceConsumersQuery,
    useGetDictionariesQuery,
    useGetDictionaryQuery,
    useGetDictionaryRecordQuery,
    useGetDowloadInfoQuery,
    useGetFileQuery,
    useGetFilesQuery,
    useGetIndexBlockAvailableQuery,
    useGetIndexBlockQuery,
    useGetIndexBlocksQuery,
    useGetMediaHeaderMutation,
    useGetMediaUnitQuery,
    useGetMediaUsedBookQuery,
    useGetMetaDataMutation,
    useGetNewContentQuery,
    useGetNewMediaQuery,
    useGetNewsQuery,
    useGetNewsTopicQuery,
    useGetNewsTopicsQuery,
    useGetNewTVQuery,
    useGetOffersMediaQuery,
    useGetOffersTVQuery,
    useGetOrboxPassedQuery,
    useGetOrboxReportQuery,
    useGetPermissionAvailUnitsQuery,
    useGetPollQuery,
    useGetPollsOptionQuery,
    useGetPollsOptionsQuery,
    useGetPollsQuery,
    useGetPortfolioAccountsQuery,
    useGetPortfolioDetailQuery,
    useGetPortfolioQuery,
    useGetProductIdReferenceBookQuery,
    useGetProductIdUserBookQuery,
    useGetProductQuery,
    useGetProductReferenceBookQuery,
    useGetProductsAccountsQuery,
    useGetProductsNewQuery,
    useGetProductsOffersQuery,
    useGetProductsQuery,
    useGetProductUsedBookQuery,
    useGetProductUserBookQuery,
    useGetPromoQuery,
    useGetPublicAdvertQuery,
    useGetPublicAdvertsQuery,
    useGetPublicBlocksQuery,
    useGetPublicNewsOneQuery,
    useGetPublicNewsQuery,
    useGetPublicPollQuery,
    useGetPublicPollsQuery,
    useGetPublicPromoQuery,
    useGetPublicPromosQuery,
    useGetReferenceBookQuery,
    useGetRegistryAccountsQuery,
    useGetRegistryQuery,
    useGetTVDBChannelQuery,
    useGetTVDBChannelsQuery,
    useGetTVDBKKAccessQuery,
    useGetTVDBKKConsumersDetailQuery,
    useGetTVDBKKConsumersQuery,
    useGetTVDBKKMonitoringQuery,
    useGetTVDBKKSignalsQuery,
    useGetTVListQuery,
    useGetTVSignalQuery,
    useGetTVSignalsQuery,
    useGetTVSignalStateQuery,
    useGetTVSignalStatesQuery,
    useGetTVUnitQuery,
    useGetTVUsedBookQuery,
    useGetUnitTrailerUploadsQuery,
    useGetUnitUploadQuery,
    useGetUnitUploadsQuery,
    useGetUploadsQuery,
    useGetUploadsV1Query,
    useGetUploadV1Query,
    useGetUsedBookQuery,
    useGetUserBookQuery,
    useLazyGetAccessPermissionDocxQuery,
    useLazyGetAccessPermissionPdfQuery,
    useLazyGetAccessPermissionQuery,
    useLazyGetAccessPermissionsAccountsQuery,
    useLazyGetAccessPermissionsQuery,
    useLazyGetAccessRequestAllowedRecipientsQuery,
    useLazyGetAccessRequestHistoryQuery,
    useLazyGetAccessRequestMessageQuery,
    useLazyGetAccessRequestMessageRecipientsQuery,
    useLazyGetAccessRequestQuery,
    useLazyGetAccessRequestsAccountsQuery,
    useLazyGetAccessRequestsQuery,
    useLazyGetAccessRequestsSummaryQuery,
    useLazyGetAdminNewsQuery,
    useLazyGetAdminNewsTopicsQuery,
    useLazyGetAdminPromoQuery,
    useLazyGetAdvertQuery,
    useLazyGetAdvertsQuery,
    useLazyGetAllAdminAdvertsQuery,
    useLazyGetAllAdminBannersQuery,
    useLazyGetAllAdminNewsQuery,
    useLazyGetAllAdminPollsQuery,
    useLazyGetAllAdminPromoQuery,
    useLazyGetAllAdvertsQuery,
    useLazyGetAllBannersQuery,
    useLazyGetAllNewsQuery,
    useLazyGetAllPollsQuery,
    useLazyGetAllPromoQuery,
    useLazyGetBannersQuery,
    useLazyGetBTechQuery,
    useLazyGetCheckedContentQuery,
    useLazyGetCheckedTVQuery,
    useLazyGetCMSFileQuery,
    useLazyGetCMSFilesQuery,
    useLazyGetContentCheckmarksFieldsQuery,
    useLazyGetContentCheckmarksQuery,
    useLazyGetContentComentsFieldsQuery,
    useLazyGetContentComentsQuery,
    useLazyGetContentDownloadInfoQuery,
    useLazyGetContentHistoryQuery,
    useLazyGetContentListQuery,
    useLazyGetContentOffersQuery,
    useLazyGetContentTVListCountQuery,
    useLazyGetContentUnitCollectionsQuery,
    useLazyGetContentUnitListCountQuery,
    useLazyGetContentUnitListQuery,
    useLazyGetContentUnitMediaQuery,
    useLazyGetContentUnitQuery,
    useLazyGetContentUserBookQuery,
    useLazyGetContractAvailUnitsQuery,
    useLazyGetContractQuery,
    useLazyGetContractsQuery,
    useLazyGetCrmConsumersAccountsQuery,
    useLazyGetCrmConsumersDetailQuery,
    useLazyGetCrmConsumersQuery,
    useLazyGetCrmProductsAccountsQuery,
    useLazyGetCrmProductsDetailQuery,
    useLazyGetCrmProductsQuery,
    useLazyGetCrmSourceConsumersAccountsQuery,
    useLazyGetCrmSourceConsumersGroupQuery,
    useLazyGetCrmSourceConsumersQuery,
    useLazyGetDictionariesQuery,
    useLazyGetDictionaryQuery,
    useLazyGetDictionaryRecordQuery,
    useLazyGetDowloadInfoQuery,
    useLazyGetFileQuery,
    useLazyGetFilesQuery,
    useLazyGetIndexBlockAvailableQuery,
    useLazyGetIndexBlockQuery,
    useLazyGetIndexBlocksQuery,
    useLazyGetMediaUnitQuery,
    useLazyGetMediaUsedBookQuery,
    useLazyGetNewContentQuery,
    useLazyGetNewMediaQuery,
    useLazyGetNewsQuery,
    useLazyGetNewsTopicQuery,
    useLazyGetNewsTopicsQuery,
    useLazyGetNewTVQuery,
    useLazyGetOffersMediaQuery,
    useLazyGetOffersTVQuery,
    useLazyGetOrboxPassedQuery,
    useLazyGetOrboxReportQuery,
    useLazyGetPermissionAvailUnitsQuery,
    useLazyGetPollQuery,
    useLazyGetPollsOptionQuery,
    useLazyGetPollsOptionsQuery,
    useLazyGetPollsQuery,
    useLazyGetPortfolioAccountsQuery,
    useLazyGetPortfolioDetailQuery,
    useLazyGetPortfolioQuery,
    useLazyGetProductIdReferenceBookQuery,
    useLazyGetProductIdUserBookQuery,
    useLazyGetProductQuery,
    useLazyGetProductReferenceBookQuery,
    useLazyGetProductsAccountsQuery,
    useLazyGetProductsNewQuery,
    useLazyGetProductsOffersQuery,
    useLazyGetProductsQuery,
    useLazyGetProductUsedBookQuery,
    useLazyGetProductUserBookQuery,
    useLazyGetPromoQuery,
    useLazyGetPublicAdvertQuery,
    useLazyGetPublicAdvertsQuery,
    useLazyGetPublicBlocksQuery,
    useLazyGetPublicNewsOneQuery,
    useLazyGetPublicNewsQuery,
    useLazyGetPublicPollQuery,
    useLazyGetPublicPollsQuery,
    useLazyGetPublicPromoQuery,
    useLazyGetPublicPromosQuery,
    useLazyGetReferenceBookQuery,
    useLazyGetRegistryAccountsQuery,
    useLazyGetRegistryQuery,
    useLazyGetTVDBChannelQuery,
    useLazyGetTVDBChannelsQuery,
    useLazyGetTVDBKKAccessQuery,
    useLazyGetTVDBKKConsumersDetailQuery,
    useLazyGetTVDBKKConsumersQuery,
    useLazyGetTVDBKKMonitoringQuery,
    useLazyGetTVDBKKSignalsQuery,
    useLazyGetTVListQuery,
    useLazyGetTVSignalQuery,
    useLazyGetTVSignalsQuery,
    useLazyGetTVSignalStateQuery,
    useLazyGetTVSignalStatesQuery,
    useLazyGetTVUnitQuery,
    useLazyGetTVUsedBookQuery,
    useLazyGetUnitTrailerUploadsQuery,
    useLazyGetUnitUploadQuery,
    useLazyGetUnitUploadsQuery,
    useLazyGetUploadsQuery,
    useLazyGetUploadsV1Query,
    useLazyGetUploadV1Query,
    useLazyGetUsedBookQuery,
    useLazyGetUserBookQuery,
    useMakeCensoredUnitMutation,
    usePostVoteMutation,
    useSendContentMutation,
    useSetAccessPermissionCommentMutation,
    useSetContentCheckmarksMutation,
    useSetContractCommentMutation,
    useUnmakeCensoredUnitMutation,
    useUpdateAccessPermissionStatusMutation,
    useUpdateAdvertsMutation,
    useUpdateBannersMutation,
    useUpdateContentCmsMutation,
    useUpdateContractMutation,
    useUpdateDictionaryRecordMutation,
    useUpdateIndexBlockMutation,
    useUpdateNewsMutation,
    useUpdateNewsTopicMutation,
    useUpdatePollsMutation,
    useUpdateProductCmsMutation,
    useUpdateProductMutation,
    useUpdatePromoMutation,
    useUpdateTVDBChannelMutation,
    useUpdateTVSignalMutation,
    useUpdateTVSignalStateMutation,
    useUploadContentMediaChunkMutation,
    useUploadMultipartMutation,
    useGetPublicRecommendQuery,
    useGetPublicRecommendsQuery,
    useLazyGetPublicRecommendQuery,
    useLazyGetPublicRecommendsQuery,
    useGetPublicMediaQuery,
    useGetPublicMediasQuery,
    useGetPublicProductQuery,
    useGetPublicProductsQuery,
    useGetPublicTVQuery,
    useGetPublicTVsQuery,
    useLazyGetPublicMediaQuery,
    useLazyGetPublicMediasQuery,
    useLazyGetPublicProductQuery,
    useLazyGetPublicProductsQuery,
    useLazyGetPublicTVQuery,
    useLazyGetPublicTVsQuery,
    useUpdatePollsOptionsMutation,
    useGetPublicNewsTopicsQuery,
    useLazyGetPublicNewsTopicsQuery,
    useGetPublicNotificationQuery,
    useGetPublicNotificationsQuery,
    useLazyGetPublicNotificationQuery,
    useLazyGetPublicNotificationsQuery,
    useCreateNotificationsMutation,
    useDeleteNotificationsMutation,
    useUpdateNotificationsMutation,
    useGetAllAdminNotificationsQuery,
    useGetAllNotificationsQuery,
    useGetNotificationQuery,
    useGetAdminNotificationQuery,
    useLazyGetAllAdminNotificationsQuery,
    useLazyGetAllNotificationsQuery,
    useLazyGetNotificationQuery,
    useLazyGetAdminNotificationQuery,
    useCreateRemoteUploadMutation,
    usePrefetch,
    useGenerateSiteOperationMutation,
    useGetOperationStatusQuery,
    useGetOperationsQuery,
    useLazyGetOperationsQuery,
    useLazyGetOperationStatusQuery,
    useGetAdvBannersQuery,
    useGetAdvBannerQuery,
    useCreateAdvBannerMutation,
    useDeleteAdvBannerMutation,
    useGetAdvBlockAvailableQuery,
    useGetAdvBlockQuery,
    useGetAdvBlocksQuery,
    useGetAdvPublicBlockQuery,
    useGetAdvPublicBlocksQuery,
    useLazyGetAdvBannerQuery,
    useLazyGetAdvBannersQuery,
    useLazyGetAdvBlockAvailableQuery,
    useLazyGetAdvBlockQuery,
    useLazyGetAdvBlocksQuery,
    useLazyGetAdvPublicBlockQuery,
    useLazyGetAdvPublicBlocksQuery,
    useUpdateAdvBannerMutation,
    useUpdateAdvBlockMutation,
    useGetPublicAdvBannerQuery,
    useGetPublicAdvBannersQuery,
    useLazyGetPublicAdvBannerQuery,
    useLazyGetPublicAdvBannersQuery,
    useGetPublicContentUnitQuery,
    useGetPublicProductContentQuery,
    useLazyGetPublicContentUnitQuery,
    useLazyGetPublicProductContentQuery,
    useCreatePublicContentMutation,
    useDeletePublicContentMutation,
    useEditPublicContentMutation,
    useGetPublicContentQuery,
    useGetPublicContentsQuery,
    useLazyGetPublicContentQuery,
    useLazyGetPublicContentsQuery,
    useCreatePublicProductMutation,
    useDeletePublicProductMutation,
    useEditPublicProductMutation,
    useGetPublicContentProductQuery,
    useGetPublicContentProductsQuery,
    useLazyGetPublicContentProductQuery,
    useLazyGetPublicContentProductsQuery,
} = contentApiV1;
